import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { ConfirmationDialogData } from '../../../models/confirmation-dialog-data.dto';
import { DefectSearchParams } from '../../../models/inspection';
import { InspectionCrudService } from '../../../services/api/inspection-crud/inspection-crud.service';
import { InspectionService } from '../../../services/api/inspection/inspection.service';
import { CubeService, MessageType } from '../../../services/cube/cube.service';
import { MigrationService } from '../../../services/migration/migration.service';
import { LoaderService } from '../../../services/loader/loader.service';
import {
  IdbService,
  WhereConditions,
} from '../../../services/storage/idb.service';
import {
  StorageService,
  STORAGE_KEYS,
} from '../../../services/storage/storage.service';
import { TemplateLoaderService } from '../../../services/template-loader/template-loader.service';
import { UserService } from '../../../services/user/user.service';
import { BridgeCombinedDefects } from '../../../shared/configs/bridge-combined-defects.config';
import { ComponentMaterialMappingObj } from '../../../shared/configs/bridge-component-material-mapping.config';
import { BridgeConstants } from '../../../shared/configs/bridge-constants.config';
import { BridgeTemplateVersion } from '../../../shared/configs/bridge-template-version.config';
import { CdAState } from '../../../shared/configs/cda-state.config';
import { CdAStatus } from '../../../shared/configs/cda-status.config';
import { DirezioneConfig } from '../../../shared/configs/direzione.config';
import { InspectionStatus } from '../../../shared/configs/inspection-status.config';
import { InspectionType } from '../../../shared/configs/inspection-type.config';
import { StructureConstants } from '../../../shared/configs/structure-constants.config';
import { StructureType } from '../../../shared/configs/structure-type.config';
import { UserRole } from '../../../shared/configs/user-role.config';
import { DuplicateCampataDialogComponent } from '../shared/components/duplicate-campata-dialog/duplicate-campata-dialog.component';
import { NewDefectDialogComponent } from '../shared/components/new-defect-dialog/new-defect-dialog.component';

import { FormlyFormOptions } from '@ngx-formly/core';
import { Subscription } from 'dexie';
import moment from 'moment';
import { from, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cube-bridge-input-template',
  templateUrl: './bridge-input-template.component.html',
  styleUrls: ['./bridge-input-template.component.scss'],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' },
    },
  ],
  standalone: false,
})
export class BridgeInputTemplateComponent implements OnInit, OnDestroy {
  bridgeConstants: typeof BridgeConstants = BridgeConstants;
  campateCount: number = 0;
  campateNoList: any[] = [];
  devMode = environment.devMode; // TEMP: For development
  direzioneList: Record<string, string>[] = [];
  fields: any[] = [];
  form: FormGroup = new FormGroup({});
  isLevel2DataChanged: boolean = false;
  isOnline: boolean = true;
  isShortcutView: boolean = false;
  l0BackupData: any = {};
  l1BackupData: any = {};
  l1InitBackupData: any = {};
  l2UpdateStatus: boolean = false;
  l3UpdateStatus: boolean = false;
  level2Backup: any = {};
  level2CampataBackup: any = {};
  level2CampateListBackup: any = [];
  levelsList: string[];
  model: any;
  options: FormlyFormOptions;
  riskIndex: number; // TODO: reuse l2UpdateIndex
  token: string;

  private allElementoList: any[];
  private appImportedInspectionId: string = '';
  private defectImageListForModification: any[] = [];
  private destroy$: Subject<void> = new Subject<void>();
  private idbRecordId: string;
  private inspectionId: string = '';
  private inspectionType: InspectionType;
  private isDisabled: boolean = false;
  private l1UpdateIndex: number;
  private l1UpdateStatus: boolean = false;
  private l2CampataNo: number;
  private l2UpdateIndex: number; // TODO: rename
  private l3UpdateComponent: string = '';
  private l3UpdateDirezione: string = '';
  private l3UpdateElementNo: number;
  private l3UpdateIndex: number;
  private massiveImportedInspection: any;
  private plannedInspection;
  private plannedInspectionId: string = '';
  private recordId: string = '';
  private repeatSectionImageListForModification: any[] = [];
  private saveStatus: boolean = false;
  private templateVersion: BridgeTemplateVersion;
  private tenantId: string;
  private unsubscribe$: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private cubeService: CubeService,
    private idbService: IdbService,
    private inspectionCrudService: InspectionCrudService,
    private inspectionService: InspectionService,
    private loaderService: LoaderService,
    private location: Location,
    private matDialog: MatDialog,
    private migrationService: MigrationService,
    private router: Router,
    private storageService: StorageService,
    private templateLoaderService: TemplateLoaderService,
    private userService: UserService
  ) {
    this.recordId = this.activatedRoute.snapshot.paramMap.get('recordId');
    this.inspectionType = +this.activatedRoute.snapshot.paramMap.get('type');
    this.templateVersion = this.activatedRoute.snapshot.paramMap.get(
      'templateVersion'
    ) as BridgeTemplateVersion;
    this.tenantId = this.activatedRoute.snapshot.paramMap.get('tenantId');
    this.storageService.addToStorage(STORAGE_KEYS.savedImages, []);
    this.storageService.addToStorage(STORAGE_KEYS.deletedFiles, []);
    this.allElementoList = this.setAllElementoList();
    this.levelsList = this.createLevelsList();
    this.model = this.createModel();
    this.options = this.createFormlyOptions();
    this.direzioneList = this.createDirezioneList();
  }

  async ngOnInit(): Promise<void> {
    await this.setIsOnline();
    if (this.isOnline) {
      this.getInspectionMediaToken().then(() => {
        this.generateTemplate();
      });
    } else {
      this.token = this.storageService.getFromStorage(
        STORAGE_KEYS.inspectionToken
      );
      this.generateTemplate();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$?.unsubscribe();
    this.clearEmptyModel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  addDefectImagesIndexToBeModified(defectList: any[], index: number): void {
    // INFO: Used in template
    for (let i = index; i < defectList.length; i++) {
      const defectTableList =
        defectList[i].modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Scheda Ispezione Ponti di Livello 1 List1'
        ];
      let indexUpdatedFileList = [];
      for (const defectTableListItem of defectTableList) {
        const key =
          defectTableListItem['Codice difetto'] ===
          BridgeConstants.eventualiNote
            ? 'Note'
            : 'N° foto';
        if (defectTableListItem[key]) {
          const fileUrls = Object.keys(defectTableListItem[key]);
          indexUpdatedFileList = indexUpdatedFileList.concat(
            fileUrls
              .map((fileUrl) => {
                if (!this.cubeService.isAlreadyUploadedFile(fileUrl))
                  return {
                    fileName: fileUrl,
                    newIndex: i,
                  };
              })
              .filter((res) => res)
          );
        }
      }
      this.defectImageListForModification =
        this.defectImageListForModification.concat(indexUpdatedFileList);
    }
  }

  addRepeatSectionImagesIndexToBeModified(
    list: any[],
    index: number,
    key: string
  ): void {
    // INFO: Used in template
    for (let i = index; i < list.length; i++) {
      if (list[i][key]) {
        const fileAlreadyInListIndex =
          this.repeatSectionImageListForModification.findIndex(
            ({ fileName }) => fileName === list[i][key]
          );

        if (fileAlreadyInListIndex !== -1)
          this.repeatSectionImageListForModification[fileAlreadyInListIndex][
            'newIndex'
          ] = i;
        else
          this.repeatSectionImageListForModification.push({
            fileName: list[i][key],
            newIndex: i,
          });
      }
    }
  }

  // TODO: needed after the temp table change?
  /**
   * Clears the empty model.
   * If the user navigates away from the page before or without saving,
   * it deletes all files in indexed db under this inspection.
   */
  @HostListener('window:beforeunload')
  clearEmptyModel(): void {
    // If user navigates away from page before/without saving
    if (
      !this.saveStatus &&
      this.inspectionType === InspectionType.newInspection
    )
      // Delete all files in indexed db under this inspection
      this.idbService.deleteAllUploadedFilesUnderInspection(
        this.model.modelHash,
        this.model.structure_type
      );
  }

  /**
   * Copies the risk sheets data for a given sheet name.
   *
   * @param sheetName - The name of the sheet to be copied.
   */
  copyRiskSheets(sheetName: string): void {
    // INFO: Used in template
    // Getting sheet data to be copied
    const sheetData = this.getDeepCopy(
      this.model[BridgeConstants.levels.level2Campata][sheetName]
    );
    const riskSheetDataForCopying = this.getRiskSheetDataForCopying(
      sheetData,
      sheetName
    );

    // Copying data to all campata
    for (let campata of this.model[BridgeConstants.levels.level2]
      .campateCdaStatus.campateList) {
      const { modelstructure: modelStructure } = campata;

      // Reset attention class of campata if there is difference between risk sheet of current campata and risk sheet to be copied
      const currentRiskSheetData = this.getRiskSheetDataForCopying(
        this.getDeepCopy(modelStructure[sheetName]),
        sheetName
      );
      if (
        JSON.stringify(currentRiskSheetData) !==
        JSON.stringify(riskSheetDataForCopying)
      ) {
        this.resetIndividualCampataAttentionClass(campata);
      }

      switch (sheetName) {
        case BridgeConstants.level2RiskSheets.strutturaleEFondazionale: {
          const pericolosita = sheetData['Stima del livello di pericolosita'];
          const vulnerabilita = sheetData['Stima del livello di vulnerabilita'];
          const esposizione = sheetData['Stima del livello di esposizione'];

          modelStructure[
            BridgeConstants.level2RiskSheets.strutturaleEFondazionale
          ] = {
            ...modelStructure[
              BridgeConstants.level2RiskSheets.strutturaleEFondazionale
            ],
            'Stima del livello di pericolosita': {
              ...(modelStructure[
                BridgeConstants.level2RiskSheets.strutturaleEFondazionale
              ]?.['Stima del livello di pericolosita'] ?? {}),
              'Classificazione strada': pericolosita['Classificazione strada'],
              'Traffico Medio Giornaliero Veicoli commerciali':
                pericolosita['Traffico Medio Giornaliero Veicoli commerciali'],
            },
            'Stima del livello di vulnerabilita': {
              ...(modelStructure[
                BridgeConstants.level2RiskSheets.strutturaleEFondazionale
              ]?.['Stima del livello di vulnerabilita'] ?? {}),
              'Anno di costruzione': vulnerabilita['Anno di costruzione'],
              'Norma di progetazione': vulnerabilita['Norma di progetazione'],
              'Schema statico': vulnerabilita['Schema statico'],
              Materiale: vulnerabilita['Materiale'],
              'Luce massima campata m': vulnerabilita['Luce massima campata m'],
            },
            'Stima del livello di esposizione': {
              ...(modelStructure[
                BridgeConstants.level2RiskSheets.strutturaleEFondazionale
              ]?.['Stima del livello di esposizione'] ?? {}),
              'Traffico medio giornaliero':
                esposizione?.['Traffico medio giornaliero'],
              'Tipologia Ente scavalcato':
                esposizione?.['Tipologia Ente scavalcato'],
              'Transporto di merci pericolose':
                esposizione?.['Transporto di merci pericolose'],
              'Luce media della campata':
                esposizione?.['Luce media della campata'],
            },
          };

          break;
        }

        case BridgeConstants.level2RiskSheets.sismica: {
          const pericolosita =
            sheetData['Valutazione del livello di pericolosità sismica'];
          const vulnerabilita =
            sheetData['Stima del livello di vulnerabilità sismica'];
          const esposizione =
            sheetData['Stima del livello di esposizione sismica'];

          modelStructure[BridgeConstants.level2RiskSheets.sismica] = {
            ...modelStructure[BridgeConstants.level2RiskSheets.sismica],
            'Valutazione del livello di pericolosità sismica': {
              ...(modelStructure[BridgeConstants.level2RiskSheets.sismica]?.[
                'Valutazione del livello di pericolosità sismica'
              ] ?? {}),
              'Categoria topografica': pericolosita['Categoria topografica'],
              'Categoria sottosuolo': pericolosita['Categoria sottosuolo'],
            },
            'Stima del livello di vulnerabilità sismica': {
              ...(modelStructure[BridgeConstants.level2RiskSheets.sismica]?.[
                'Stima del livello di vulnerabilità sismica'
              ] ?? {}),
              Materiale: vulnerabilita['Materiale'],
              Campate: vulnerabilita['Campate'],
              'Criteri di progettazione':
                vulnerabilita['Criteri di progettazione'],
              'Luce massima campata m': vulnerabilita['Luce massima campata m'],
            },
            'Stima del livello di esposizione sismica': {
              ...(modelStructure[BridgeConstants.level2RiskSheets.sismica]?.[
                'Stima del livello di esposizione sismica'
              ] ?? {}),
              "Strategità dell'opera": esposizione?.["Strategità dell'opera"],
            },
          };

          break;
        }

        case BridgeConstants.level2RiskSheets.frane: {
          const suscettibilita =
            sheetData[
              'Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'
            ];
          const vulnerabilita =
            sheetData[
              'Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane'
            ];

          modelStructure[BridgeConstants.level2RiskSheets.frane] = {
            ...modelStructure[BridgeConstants.level2RiskSheets.frane],
            'Fenomeno trascurabile': sheetData['Fenomeno trascurabile'],
            'Stima Del Livello Di Suscettibilità Legato Al Rischio Frane': {
              ...(modelStructure[BridgeConstants.level2RiskSheets.frane]?.[
                'Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'
              ] ?? {}),
              'Stato di attività': suscettibilita['Stato di attività'],
              'Massima velocità attesa':
                suscettibilita['Massima velocità attesa'],
              'Magnitudo attesa su base volumetrica in m3':
                suscettibilita['Magnitudo attesa su base volumetrica in m3'],
              'Misure di mitigazione': suscettibilita['Misure di mitigazione'],
            },
            'Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane': {
              ...(modelStructure[BridgeConstants.level2RiskSheets.frane]?.[
                'Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane'
              ] ?? {}),
              'Estensione Interferenza':
                vulnerabilita['Estensione Interferenza'],
              'Presenza di fondazioni superficiali o comunque NON progettate per resistere alle azioni orizzontali':
                vulnerabilita[
                  'Presenza di fondazioni superficiali o comunque NON progettate per resistere alle azioni orizzontali'
                ],
            },
          };

          break;
        }

        case BridgeConstants.level2RiskSheets.idraulica: {
          const suscettibilita =
            sheetData['Stima della suscettibilita legeta al rischio idraulico'];
          const vulnerabilita =
            sheetData['Stima della vulnerabilita legeta al rischio idraulico'];

          modelStructure[BridgeConstants.level2RiskSheets.idraulica] = {
            ...modelStructure[BridgeConstants.level2RiskSheets.idraulica],
            'Fenomeno trascurabile': sheetData['Fenomeno trascurabile'],
            'Stima della suscettibilita legeta al rischio idraulico': {
              ...(modelStructure[BridgeConstants.level2RiskSheets.idraulica]?.[
                'Stima della suscettibilita legeta al rischio idraulico'
              ] ?? {}),
              'Franco idraulico atteso':
                suscettibilita['Franco idraulico atteso'],
              'a Diametro pila': suscettibilita['a Diametro pila'],
            },
            'Stima della vulnerabilita legeta al rischio idraulico': {
              ...(modelStructure[BridgeConstants.level2RiskSheets.idraulica]?.[
                'Stima della vulnerabilita legeta al rischio idraulico'
              ] ?? {}),
              'Fenomeni di deposizione':
                vulnerabilita['Fenomeni di deposizione'],
              'Transporto di materiale vegetale':
                vulnerabilita['Transporto di materiale vegetale'],
              'Dimensioni del bacino idrografico km2':
                vulnerabilita['Dimensioni del bacino idrografico km2'],
            },
          };

          break;
        }
      }
    }

    this.cubeService.showMessage(
      MessageType.Info,
      'templates.data_copied_successfully',
      { sheetName }
    );
  }

  /**
   * Adds defect files uploaded to the database or IndexedDB to the list of deleted files in local storage. Called when campata / defect template is deleted.
   *
   * @param urlList - An array of tuples containing the file URL and its corresponding key.
   * @returns void
   */
  deleteUploadedDefectFiles(urlList: [string, string][]): void {
    // INFO: Used in template
    // Deleting each defect file
    const deletedFilesList = urlList.map(([fotoKey, foto]) => ({
      fileName: this.cubeService.isAlreadyUploadedFile(foto)
        ? foto // File is saved in DB
        : fotoKey, // File is saved in indexedDB
      isDefect: true,
    }));

    this.storageService.addDeletedFileUrlsToStorage(deletedFilesList);
  }

  /**
   * Adds non-defect files uploaded to the database or IndexedDB to the deleted files list in local storage.
   *
   * @param fileNames - The name or names of the files to be deleted.
   */
  deleteUploadedFiles(fileNames: string | string[]): void {
    // INFO: Used in template
    if (!fileNames.length) return;

    // Adding files to deleted files list in local storage
    let deletedFileUrlsList: any[];
    if (Array.isArray(fileNames)) {
      // fileNames is a list of file names
      deletedFileUrlsList = fileNames.map((fileName) => ({ fileName }));
    } else {
      // fileNames is a single file name
      deletedFileUrlsList = [{ fileName: fileNames }];
    }

    // Updating deleted files list in local storage
    this.storageService.addDeletedFileUrlsToStorage(deletedFileUrlsList);
  }

  duplicateDefect(
    index: number,
    list: any[],
    type: string,
    listName: string
  ): void {
    // INFO: Used in template
    const duplicateObject = this.getDeepCopy(list[index]);
    const component = this.getItemFromListByValue(
      ComponentMaterialMappingObj[type].component,
      duplicateObject.modelstructure.component
    );

    const direzioneNumber = duplicateObject.direzione
      ? `-${DirezioneConfig[duplicateObject.direzione].number}`
      : '';

    const componentCountKey = `${duplicateObject['element no']}-${type}-${duplicateObject.modelstructure.component}${direzioneNumber}`;
    const componentNo =
      this.model[BridgeConstants.levels.l2].componentCount[componentCountKey] +
      1;
    const arr = duplicateObject.uniquecode.split('.');
    const direzioneValue = arr.length === 5 ? '.' + arr[4] : '';
    const uniqueCode = `${arr[0]}.${arr[1]}.${arr[2]}.${componentNo}${direzioneValue}`;

    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'Cod unique'
    ] = uniqueCode;
    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'uniquecode'
    ] = uniqueCode;

    // Updating sigla id elem
    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'Sigla ID Elem'
    ] = `C${this.model[BridgeConstants.levels.l2][BridgeConstants.partNo]}-${
      ComponentMaterialMappingObj[type].element_code
    }${duplicateObject['element no']}${
      duplicateObject.modelstructure.direzione ?? ''
    }-${component.component_code}${componentNo}`;

    // Updating Codice Elemento
    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'Codice Elemento'
    ] =
      ComponentMaterialMappingObj[type].element_code +
      duplicateObject['element no'];

    // Updating Codice Componento
    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'Codice Componento'
    ] = component.component_code + componentNo;

    // Updating codice elemento
    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'codice elemento'
    ] = `${ComponentMaterialMappingObj[type].element_code}`;

    // Updating component code
    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'Component code'
    ] = `${component.component_code}`;

    // Updating localizzazione
    duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
      'Localizzazione'
    ] = `${duplicateObject.modelstructure.component} - ${component.component_code}${componentNo}`;

    /* // INFO: Commenting as per RINA-6309
    // Updating fotoCount value in existing images
    let fotoCount = this.model.fotoCount;
    const originalFotoCount = this.model.fotoCount; */
    const defectsList: any[] =
      duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ];

    defectsList.forEach((defectRow, defectRowIndex) => {
      // INFO: Similar functionality is also used in duplicateIndexedDbFiles() and in duplicate-campata-dialog.component.ts file
      if (
        defectRow['Codice difetto'] === BridgeConstants.eventualiNote
          ? !Object.keys(defectRow.Note).length
          : !defectRow['N° foto']
      )
        return;

      // If defect has images
      const updatedNFoto = '';
      const key =
        defectRow['Codice difetto'] === BridgeConstants.eventualiNote
          ? 'Note'
          : 'N° foto';
      /* // INFO: Commenting as per RINA-6309
      const nFotoValues = Object.entries(defectRow[key]);
      nFotoValues.forEach(([fotoKey, foto]) => {
        const nameParts = fotoKey.split(' ');
        fotoCount++;
        nameParts[1] = fotoCount;
        fotoKey = nameParts.join(' ');
        updatedNFoto[fotoKey] = foto;
      }); */

      // Updating N° foto
      duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ][defectRowIndex][key] = updatedNFoto;
    });

    /* // INFO: Commenting as per RINA-6309
    this.inspectionService.setImageCount$(fotoCount); */

    // Updating fotoCode
    const direzione = duplicateObject.modelstructure.direzione
      ? '- ' +
        DirezioneConfig[duplicateObject.modelstructure.direzione].fullName
      : '';

    duplicateObject.modelstructure[
      'Scheda Ispezione Ponti di Livello 1'
    ].fotoCode = `${type} ${duplicateObject['element no']} ${direzione} (${ComponentMaterialMappingObj[type].element_code}${duplicateObject['element no']}) - ${duplicateObject.modelstructure.component} (${componentNo})`;

    // Updating localizzazione
    duplicateObject.localizazione =
      duplicateObject.modelstructure[
        'Scheda Ispezione Ponti di Livello 1'
      ].Localizzazione;

    // Updating uniquecode
    duplicateObject.uniquecode =
      duplicateObject.modelstructure[
        'Scheda Ispezione Ponti di Livello 1'
      ].uniquecode;

    this.model[BridgeConstants.levels.l2][listName] = list.concat([
      duplicateObject,
    ]);
    this.model[BridgeConstants.levels.l2].componentCount[
      componentCountKey
    ] += 1;

    /* // INFO: Commenting as per RINA-6309
    // Duplicating files under this defect in indexedDB
    this.duplicateIndexedDbFiles(
      this.model[BridgeConstants.levels.l2][listName].length - 1,
      index,
      true,
      originalFotoCount,
      listName,
      duplicateObject.modelstructure[BridgeConstants.partNo]
    ); */

    this.refreshFields();
  }

  async getCampataAttentionClass(): Promise<void> {
    // INFO: Used in template
    if (this.isOnline) {
      const formData = this.getCampataAttentionClassFormData();

      this.inspectionService
        .calculateAttentionClass(formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.isLevel2DataChanged = false;
          this.setCampataAttentionClass(res.data);
        });
    } else {
      this.cubeService.showMessage(
        MessageType.Info,
        'templates.offline_to_calculate_cda'
      );
    }
  }

  getCdaClassName(value: any): string | null {
    // INFO: Used in template

    if (typeof value === 'boolean' || typeof value === 'number') return null;

    const cdAStatusList = this.getCdAStatusList();

    return cdAStatusList.some(
      (status) => status?.toLowerCase() === value?.toLowerCase()
    )
      ? value.replace(/\s/g, '').toLowerCase()
      : null;
  }

  getConfirmation(dialogData?: ConfirmationDialogData): Promise<boolean> {
    // INFO: Used in template
    return this.cubeService.confirm(dialogData).toPromise();
  }

  getDeepCopy(item: any): any {
    // INFO: Used in template
    return this.cubeService.getDeepCopy(item);
  }

  getDefectsList(
    level: string,
    component: string,
    material: string = ''
  ): void {
    // INFO: Used in template
    if (this.isOnline) {
      this.fetchDefectsList({
        component,
        element: level,
        level,
        material,
      });
    } else {
      this.fetchDefectsListFromIndexedDb({
        component,
        element: level,
        level,
        material,
      });
    }
  }

  getElementiAccessoriDefects(): void {
    // INFO: Used in template
    this.getDefectsList(
      BridgeConstants.elementiAccessori,
      BridgeConstants.elementiAccessori,
      ''
    );
  }

  getFraneOrIdraulicaRiskSheetDefaultValues(sheetName: string): any {
    // INFO: Used in template
    const sheetModel = this.getDeepCopy(
      this.model[this.bridgeConstants.levels.level2Campata][sheetName]
    );
    const resetValues = this.getRiskSheetDefaultValues(sheetName);

    return this.resetLevel2CampateUserInputtedValues(
      sheetModel,
      resetValues[sheetName]
    );
  }

  getItemFromListByValue(list, value): any {
    // INFO: Used in template
    return list.find((item) => item.value === value);
  }

  getLevel1TipologiaElementiStructures(): void {
    // INFO: Used in template
    this.getDefectsList(
      'tipologiaElementiStructures',
      'tipologiaElementiStructures',
      ''
    );
  }

  hasValue(value: any): boolean {
    // INFO: Used in template
    return value != null && value !== '';
  }

  isCampataPresentInL1(campataNo: number): boolean {
    // INFO: Used in template
    const l1CampateList =
      this.model[BridgeConstants.levels.l0].opereparzialilist?.[0]
        ?.modelstructure?.campatelist ?? [];

    const isCampataPresentInL1 = l1CampateList.some(
      (l1Campata) => l1Campata[BridgeConstants.partNo] === campataNo
    );

    return isCampataPresentInL1;
  }

  isFieldDisabled(
    rolesList: UserRole[],
    isDisabled: boolean,
    isNotAllowed: boolean = false
  ): boolean {
    // INFO: Used in template
    if (isDisabled) return true;

    const isUserAllowedByRole = rolesList.length
      ? this.userService.isUserAllowedByRole(rolesList)
      : true;

    return isNotAllowed ? isUserAllowedByRole : !isUserAllowedByRole;
  }

  isLivelloDiDifettositaValueChanged(value: string): boolean {
    // INFO: Used in template
    return ['ALTO', 'MEDIO-ALTO', 'MEDIO', 'MEDIO-BASSO', 'BASSO'].includes(
      value
    );
  }

  isSaveButtonDisabled(): boolean {
    // Check if active level is level 2 and if level 2 data has changed
    if (
      this.model.activeLevel === this.bridgeConstants.levels.level2 &&
      this.isLevel2DataChanged
    )
      return true;

    // Check if active level is level 2 or level 2 campata and if app is offline
    if (
      [
        this.bridgeConstants.levels.level2,
        this.bridgeConstants.levels.level2Campata,
      ].includes(this.model.activeLevel) &&
      !this.isOnline
    )
      return true;

    return false;
  }

  migrateDataToChildLevel(
    index: number,
    destinationLevel: string,
    sourceList,
    elementNo: string
  ): void {
    // INFO: Used in template
    switch (destinationLevel) {
      case BridgeConstants.levels.l1:
        this.campateCount = sourceList[0].modelstructure.campatelist.length;
        this.saveDeletedFilesToCurrentLevel(
          this.model[BridgeConstants.levels.l0]
        );
        this.l0BackupData = this.getDeepCopy(
          this.model[BridgeConstants.levels.l0]
        );
        this.l1UpdateStatus = true;
        this.l1UpdateIndex = index;

        break;

      case BridgeConstants.levels.l2:
        this.saveDeletedFilesToCurrentLevel(
          this.model[BridgeConstants.levels.l1]
        );
        this.l1BackupData = this.getDeepCopy(
          this.model[BridgeConstants.levels.l1]
        );
        this.l2UpdateStatus = true;
        this.l2UpdateIndex = index;
        this.l2CampataNo = sourceList[index][BridgeConstants.partNo];

        break;

      case BridgeConstants.levels.level2:
        this.level2Backup = this.getDeepCopy(
          this.model[BridgeConstants.levels.level2]
        );

        break;

      case BridgeConstants.levels.level2Campata:
        this.level2CampateListBackup = this.getDeepCopy(sourceList);
        this.riskIndex = index;
        this.level2CampataBackup = this.getDeepCopy(sourceList[index]);

        break;

      case BridgeConstants.levels.spalle: // Defects Template - Spalle
      case BridgeConstants.levels.pile: // Defects Template - Pile
      case BridgeConstants.levels.archi: // Defects Template - Archi
      case BridgeConstants.levels.impalcati: // Defects Template - Impalcati
      case BridgeConstants.levels.giunti: // Defects Template - Giunti
      case BridgeConstants.levels.apparecchiSismici: // Defects Template - Apparecchi Sismici
      case BridgeConstants.levels.fileDiAppoggi: // Defects Template - File di Appoggi
        this.l3UpdateStatus = true;
        this.l3UpdateIndex = index;
        this.inspectionService.setDefectRowIndex$(index);
        this.l3UpdateComponent = sourceList[index].modelstructure.component;
        this.l3UpdateDirezione = sourceList[index].modelstructure.direzione;
        this.l3UpdateElementNo = sourceList[index].modelstructure[elementNo];
        this.model[destinationLevel].component =
          sourceList[index].modelstructure.component;
        this.model[destinationLevel].materiale =
          sourceList[index].modelstructure.materiale;

        /* // Functionality now handled by syncSharedModelDataForList(). The following code can be removed at a later stage.
        // Setting Codice IOP for defect levels
        for (const source of sourceList) {
          source.modelstructure['Scheda Ispezione Ponti di Livello 1'][
            'Codice IOP'
          ] = this.model[BridgeConstants.levels.l0]['Codice IOP'];
        } */
        this.saveDeletedFilesToCurrentLevel(
          this.model[BridgeConstants.levels.l2]
        );

        break;
    }

    this.model[destinationLevel] ??= {};
    this.form = new FormGroup({});
    this.model[destinationLevel] = this.getDeepCopy(
      sourceList[index].modelstructure
    );
    this.options.formState[destinationLevel] = false;
  }

  onChangeElementiAccessoriCasoOption(): void {
    // INFO: Used in template
    this.resetCasoTableValues();
  }

  onClickBack(): void {
    if (this.isShortcutView) {
      // Disabling shortcut view
      this.isShortcutView = false;

      // Navigating to Level 2 // TODO - try to navigate back to the correct tab
      // this.model.activeLevel = BridgeConstants.levels.level2Campata;
      this.switchActiveLevel(BridgeConstants.levels.level2Campata);
      this.options.formState.l1Descrizione = true;
      this.options.formState.l1Frane = false;

      // Restoring backup data to both the levels
      this.model[BridgeConstants.levels.l0] = this.l0BackupData;
      this.model[BridgeConstants.levels.level2Campata] =
        this.level2CampataBackup;

      this.refreshFields();

      return;
    }

    this.cubeService
      .confirm({
        message: `templates.${
          this.model.activeLevel === BridgeConstants.levels.l0
            ? 'go_back_home_page'
            : 'unsaved_progress'
        }`,
      })
      .pipe(
        filter((isConfirmed) => isConfirmed),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.goBack();
      });
  }

  onClickGotoLevel(level: string, level1SubSection?: string): void {
    // INFO: Used in template
    // Setting shortcut view
    this.isShortcutView = true;

    // Taking backup of current data and l0 data
    this.l0BackupData = this.getDeepCopy(this.model[BridgeConstants.levels.l0]);
    this.level2CampataBackup = this.getDeepCopy(
      this.model[BridgeConstants.levels.level2Campata]
    );

    // Navigating to the selected level
    switch (level) {
      case BridgeConstants.levels.l0:
        // this.model.activeLevel = BridgeConstants.levels.l0;
        this.switchActiveLevel(BridgeConstants.levels.l0);

        break;

      case BridgeConstants.levels.l1:
        this.model[BridgeConstants.levels.l1] = this.getDeepCopy(
          this.model[BridgeConstants.levels.l0].opereparzialilist[0]
            .modelstructure
        );

        // Setting the correct subsection
        this.options.formState.l1Descrizione =
          level1SubSection === 'descrizione' ? true : false;
        this.options.formState.l1Frane =
          level1SubSection === 'descrizione' ? false : true;
        // this.model.activeLevel = BridgeConstants.levels.l1;
        this.switchActiveLevel(BridgeConstants.levels.l1);

        break;
    }

    this.refreshFields();
  }

  async onClickSave(): Promise<void> {
    let isValid: boolean;
    switch (this.model.activeLevel) {
      case BridgeConstants.levels.l0: // Level 0
        isValid = await this.validateL0();
        if (isValid) this.saveModelToIndexedDb();

        break;

      case BridgeConstants.levels.l1: // Level 1
        isValid = await this.validateL1();
        if (isValid) this.migrateDataFromLevel1ToLevel0();

        break;

      case BridgeConstants.levels.l2: // Level 2 (L2)
        isValid = await this.validateL2();
        if (isValid) this.migrateDataFromLevel2ToLevel1();

        break;

      case BridgeConstants.levels.level2: // Level 2
        await this.migrateDataFromLevel2ToLevel0();
        // TODO - check if more steps are needed
        break;

      case BridgeConstants.levels.spalle: // Defects Template - Spalle
        isValid = await this.validateDefectLevel();
        if (isValid)
          this.migrateDataFromDefectLevelsToLevel2(
            BridgeConstants.defectElements.spalle
          );

        break;

      case BridgeConstants.levels.pile: // Defects Template - Pile
        isValid = await this.validateDefectLevel();
        if (isValid)
          this.migrateDataFromDefectLevelsToLevel2(
            BridgeConstants.defectElements.pile
          );

        break;

      case BridgeConstants.levels.archi: // Defects Template - Archi
        isValid = await this.validateDefectLevel();
        if (isValid)
          this.migrateDataFromDefectLevelsToLevel2(
            BridgeConstants.defectElements.archi
          );

        break;

      case BridgeConstants.levels.impalcati: // Defects Template - Impalcati
        isValid = await this.validateDefectLevel();
        if (isValid)
          this.migrateDataFromDefectLevelsToLevel2(
            BridgeConstants.defectElements.impalcati
          );

        break;

      case BridgeConstants.levels.giunti: // Defects Template - Giunti
        isValid = await this.validateDefectLevel();
        if (isValid)
          this.migrateDataFromDefectLevelsToLevel2(
            BridgeConstants.defectElements.giunti
          );

        break;

      case BridgeConstants.levels.apparecchiSismici: // Defects Template - Apparecchi Sismici
        isValid = await this.validateDefectLevel();
        if (isValid)
          this.migrateDataFromDefectLevelsToLevel2(
            BridgeConstants.defectElements.apparecchiSismici
          );

        break;

      case BridgeConstants.levels.fileDiAppoggi: // Defects Template - File di Appoggi
        isValid = await this.validateDefectLevel();
        if (isValid)
          this.migrateDataFromDefectLevelsToLevel2(
            BridgeConstants.defectElements.fileDiAppoggi
          );

        break;

      case BridgeConstants.levels.level2Campata: // Risk Template
        this.migrateDataFromRiskSheetsToLevel2();

        break;
    }
  }

  openDuplicateCampataDialog(selectedIndex: number): void {
    // INFO: Used in template
    const dialogRef = this.matDialog.open(DuplicateCampataDialogComponent, {
      width: '550px',
      data: {
        campateNoList: this.campateNoList,
        existingCampateList: this.model[BridgeConstants.levels.l1].campatelist,
        selectedIndex,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((res) => res != null),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.model[BridgeConstants.levels.l1].campatelist = this.model[
          BridgeConstants.levels.l1
        ].campatelist.concat([res.duplicateCampata]);
        /* // INFO: Commenting as per RINA-6309
        this.duplicateIndexedDbFiles(
          res.duplicateCampata[BridgeConstants.partNo],
          res.selectedCampataNo,
          false,
          originalFotoCount
        ); */
        this.campateCount++;
        this.refreshFields();
      });
  }

  openNewDefectDialog(level: string, type: string, rowIndex = null): void {
    // INFO: Used in template
    const dataList =
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ];

    const dialogRef = this.matDialog.open(NewDefectDialogComponent, {
      width: '550px',
      data: {
        defectType: type,
        editData:
          type == BridgeConstants.defectDialog.editDefect
            ? dataList[rowIndex]
            : null,
        elementoList: this.allElementoList,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => result != null),
        takeUntil(this.destroy$)
      )
      .subscribe(({ type, data }) => {
        if (!data) return;

        let defectData;
        if (type !== BridgeConstants.defectDialog.deleteDefect) {
          defectData = {
            'Codice difetto': data.codice_difetto,
            'Descrizione difetto': data.descrizione_difetto,
            visto: false,
            G: JSON.stringify(data.G),
            'Estensione K1': null,
            'Intensità K2': null,
            'N° foto': '',
            'K1 options': {
              1: data.k1_3,
              0.2: data.k1_1,
              0.5: data.k1_2,
            },
            'K2 options': {
              1: data.k2_3,
              0.2: data.k2_1,
              0.5: data.k2_2,
            },
            PS: '',
            NA: false,
            NR: false,
            NP: false,
            'Sup Est Dif': '',
            Note: '',
            component: this.model[level].component,
            material: this.model[level].materiale,
            tipologiaDifetto: data.tipologiaDifetto, // Elemento for filtering inf defect dialog
            fotoName:
              this.model[level]['Scheda Ispezione Ponti di Livello 1'].fotoCode,
          };
        }

        switch (type) {
          case BridgeConstants.defectDialog.addDefect:
            this.updateEventualiNoteFilesRowIndex(1);
            const isDefectPresent = dataList.some(
              (defect) =>
                defect['Codice difetto'] === data.codice_difetto &&
                defect['Descrizione difetto'] === data.descrizione_difetto
            );

            if (isDefectPresent) break;

            const addedData = defectData;
            dataList.splice(dataList.length - 1, 0, addedData);

            break;

          case BridgeConstants.defectDialog.editDefect:
            dataList[rowIndex] = defectData;

            break;

          case BridgeConstants.defectDialog.deleteDefect:
            this.updateEventualiNoteFilesRowIndex(-1);
            dataList.splice(rowIndex, 1);

            break;
        }

        this.form = new FormGroup({});

        this.model[level]['Scheda Ispezione Ponti di Livello 1'][
          'Scheda Ispezione Ponti di Livello 1 List1'
        ] = [...dataList];
        this.options.formState[level] = false;
      });
  }

  refreshFields(): void {
    // INFO: Used in template
    // need to update funtion name after the functionality is verified
    this.model = { ...this.model };
  }

  async resetAllCampataAttentionClass(): Promise<void> {
    // INFO: Used in template
    const level2 = this.getDeepCopy(
      this.model[BridgeConstants.levels.l0].level2?.level2List?.[0]
    );

    if (!level2) return;

    const cdaStatusList = this.getCdAStatusList();
    const campateList =
      level2?.modelstructure?.campateCdaStatus?.campateList ?? [];

    const isAttentionClassCalculated = campateList
      .map((campata: any) =>
        [
          BridgeConstants.level2RiskSheets.strutturaleEFondazionale,
          BridgeConstants.level2RiskSheets.sismica,
          BridgeConstants.level2RiskSheets.frane,
          BridgeConstants.level2RiskSheets.idraulica,
          'CdA',
        ].some((key) => cdaStatusList.includes(campata[key]))
      )
      .some((value) => value);

    if (!isAttentionClassCalculated) return;

    // Resetting the values if CdA status is present
    await this.getConfirmation({
      isCancelButtonVisible: false,
      message: 'templates.cda_calculation_reset',
      title: 'confirmation_dialog.title.info',
    });

    level2['Cda Status'] = CdAState.notCompleted;

    if (!level2?.modelstructure) return;

    const campateCdaStatus = level2.modelstructure.campateCdaStatus;
    campateCdaStatus["classeDiAttenzioneDell'Opera"] = CdAState.na;
    campateCdaStatus['calculatedCdAValue'] = CdAState.na;

    if (campateCdaStatus['campateList'].length) {
      campateCdaStatus['campateList'].forEach((campata) => {
        campata[BridgeConstants.level2RiskSheets.strutturaleEFondazionale] = '';
        campata[BridgeConstants.level2RiskSheets.sismica] = '';
        campata[BridgeConstants.level2RiskSheets.frane] = '';
        campata[BridgeConstants.level2RiskSheets.idraulica] = '';
        campata['CdA'] = '';

        const {
          [BridgeConstants.level2RiskSheets.strutturaleEFondazionale]:
            strutturaleEFondazionale,
          [BridgeConstants.level2RiskSheets.sismica]: sismica,
          [BridgeConstants.level2RiskSheets.frane]: frane,
          [BridgeConstants.level2RiskSheets.idraulica]: idraulica,
          [BridgeConstants.level2RiskSheets.globale]: globale,
        } = campata.modelstructure;

        if (strutturaleEFondazionale) {
          strutturaleEFondazionale['Stima del livello di pericolosita'][
            'Classe di pericolosita'
          ] = '';
          strutturaleEFondazionale['Stima del livello di vulnerabilita'][
            'Classe di vulnerabilita'
          ] = '';
          strutturaleEFondazionale['Stima del livello di esposizione'][
            'Classe di esposizione'
          ] = '';
          strutturaleEFondazionale[
            'Stima della classe di attenzione strutturale e fondazionale'
          ]['Classe di attenzione'] = '';
        }

        if (sismica) {
          sismica['Valutazione del livello di pericolosità sismica'][
            'Classe di Pericolosita'
          ] = '';
          sismica['Stima del livello di vulnerabilità sismica'][
            'Classe di vulnerabilita'
          ] = '';
          sismica['Stima del livello di esposizione sismica'][
            'Classe di esposizione'
          ] = '';
          sismica[
            'Stima della classe di attenzione sismica a livello territoriale'
          ]['Classe di attenzione'] = '';
        }

        if (frane) {
          frane['Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'][
            'Classe Di Suscettibilita'
          ] = '';
          frane['Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane'][
            'Classe Di Vulnerabilita'
          ] = '';
          frane['Stima Del Livello Di Esposizione Legato Al Rischio Frane'][
            'Classe Di Esposizione Rischio Frane'
          ] = '';
          frane[
            'Stima Della Classe Di Attenzione Frane A Livello Territoriale'
          ]['Classe Di Attenzione'] = '';
        }

        if (idraulica) {
          const suscettibilita =
            idraulica['Stima della suscettibilita legeta al rischio idraulico'];
          const vulnerabilita =
            idraulica['Stima della vulnerabilita legeta al rischio idraulico'];
          const esposizione =
            idraulica['Stima dell espezione legata al rischio idraulico'];
          const classeDiAttenzione =
            idraulica['Stima della classe di attenzione rischio idraulico'];

          suscettibilita[
            'Classe di suscettibilita per il fenomeno di sormonto'
          ] = '';

          suscettibilita[
            'Classe di suscettibilita per il fenomeno di erosione generalizzata'
          ] = '';

          suscettibilita[
            'Classe di suscettibilita per il fenomeno di erosione localizzata'
          ] = '';

          vulnerabilita['Classe di vulnerabilita per il fenomeno di sormonto'] =
            '';
          vulnerabilita[
            'Classe di vulnerabilita per il fenomeno di erosione generalizzata'
          ] = '';
          vulnerabilita[
            'Classe di vulnerabilita per il fenomeno di erosione localizzata'
          ] = '';

          esposizione['Classe di esposizione'] = '';
          classeDiAttenzione['Classe di attenzione rischio sormonto'] = '';
          classeDiAttenzione[
            'Classe di attenzione rischio erosione generalizzata'
          ] = '';
          classeDiAttenzione[
            'Classe di attenzione rischio erosione localizzata'
          ] = '';
          classeDiAttenzione['Classe di attenzione rischio erosione'] = '';

          classeDiAttenzione['Classe di attenzione rischio idraulico'] = '';
        }

        if (globale) {
          globale['Classe Di Attenzione Strutturale Fondazionale'] = '';
          globale['Classe Di Attenzione Sismica'] = '';
          globale['Classe Di Attenzione Frane'] = '';
          globale['Classe Di Attenzione Idraulica'] = '';
          globale['Classe Di Attenzione Globale Della Campata'] = '';
        }
      });
    }

    this.model[BridgeConstants.levels.l0].level2.level2List = [].concat(level2);
  }

  async resetIndividualCampataAttentionClass(campata: any): Promise<void> {
    campata[BridgeConstants.level2RiskSheets.strutturaleEFondazionale] = '';
    campata[BridgeConstants.level2RiskSheets.sismica] = '';
    campata[BridgeConstants.level2RiskSheets.frane] = '';
    campata[BridgeConstants.level2RiskSheets.idraulica] = '';
    campata['CdA'] = '';

    const {
      [BridgeConstants.level2RiskSheets.strutturaleEFondazionale]:
        strutturaleEFondazionale,
      [BridgeConstants.level2RiskSheets.sismica]: sismica,
      [BridgeConstants.level2RiskSheets.frane]: frane,
      [BridgeConstants.level2RiskSheets.idraulica]: idraulica,
      [BridgeConstants.level2RiskSheets.globale]: globale,
    } = campata.modelstructure;

    if (strutturaleEFondazionale) {
      strutturaleEFondazionale['Stima del livello di pericolosita'][
        'Classe di pericolosita'
      ] = '';
      strutturaleEFondazionale['Stima del livello di vulnerabilita'][
        'Classe di vulnerabilita'
      ] = '';
      strutturaleEFondazionale['Stima del livello di esposizione'][
        'Classe di esposizione'
      ] = '';
      strutturaleEFondazionale[
        'Stima della classe di attenzione strutturale e fondazionale'
      ]['Classe di attenzione'] = '';
    }

    if (sismica) {
      sismica['Valutazione del livello di pericolosità sismica'][
        'Classe di Pericolosita'
      ] = '';
      sismica['Stima del livello di vulnerabilità sismica'][
        'Classe di vulnerabilita'
      ] = '';
      sismica['Stima del livello di esposizione sismica'][
        'Classe di esposizione'
      ] = '';
      sismica[
        'Stima della classe di attenzione sismica a livello territoriale'
      ]['Classe di attenzione'] = '';
    }

    if (frane) {
      frane['Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'][
        'Classe Di Suscettibilita'
      ] = '';
      frane['Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane'][
        'Classe Di Vulnerabilita'
      ] = '';
      frane['Stima Del Livello Di Esposizione Legato Al Rischio Frane'][
        'Classe Di Esposizione Rischio Frane'
      ] = '';
      frane['Stima Della Classe Di Attenzione Frane A Livello Territoriale'][
        'Classe Di Attenzione'
      ] = '';
    }

    if (idraulica) {
      const suscettibilita =
        idraulica['Stima della suscettibilita legeta al rischio idraulico'];
      const vulnerabilita =
        idraulica['Stima della vulnerabilita legeta al rischio idraulico'];
      const esposizione =
        idraulica['Stima dell espezione legata al rischio idraulico'];
      const classeDiAttenzione =
        idraulica['Stima della classe di attenzione rischio idraulico'];

      suscettibilita['Classe di suscettibilita per il fenomeno di sormonto'] =
        '';

      suscettibilita[
        'Classe di suscettibilita per il fenomeno di erosione generalizzata'
      ] = '';

      suscettibilita[
        'Classe di suscettibilita per il fenomeno di erosione localizzata'
      ] = '';

      vulnerabilita['Classe di vulnerabilita per il fenomeno di sormonto'] = '';
      vulnerabilita[
        'Classe di vulnerabilita per il fenomeno di erosione generalizzata'
      ] = '';
      vulnerabilita[
        'Classe di vulnerabilita per il fenomeno di erosione localizzata'
      ] = '';

      esposizione['Classe di esposizione'] = '';
      classeDiAttenzione['Classe di attenzione rischio sormonto'] = '';
      classeDiAttenzione[
        'Classe di attenzione rischio erosione generalizzata'
      ] = '';
      classeDiAttenzione['Classe di attenzione rischio erosione localizzata'] =
        '';
      classeDiAttenzione['Classe di attenzione rischio erosione'] = '';

      classeDiAttenzione['Classe di attenzione rischio idraulico'] = '';
    }

    if (globale) {
      globale['Classe Di Attenzione Strutturale Fondazionale'] = '';
      globale['Classe Di Attenzione Sismica'] = '';
      globale['Classe Di Attenzione Frane'] = '';
      globale['Classe Di Attenzione Idraulica'] = '';
      globale['Classe Di Attenzione Globale Della Campata'] = '';
    }
  }

  resetParentForm(fieldForm: FormGroup): void {
    // INFO: Used in template
    Object.keys(fieldForm.controls).forEach((key) => {
      const control = fieldForm.get(key);
      if (control instanceof FormGroup) {
        this.resetParentForm(control);
      } else {
        control.reset(null);
      }
    });
  }

  saveModelToIndexedDbForPlannedInspection(): void {
    // INFO: Used in template
    if (
      ![
        InspectionType.massiveImportedInspection,
        InspectionType.plannedInspection,
        InspectionType.submittedInspection,
      ].includes(this.inspectionType)
    ) {
      this.loaderService.requestEnded();

      return;
    }

    // Checking if model already exists in indexedDB
    this.idbService.getInspectionModelsList().then((res) => {
      // Below condition is only applicable in a specific scenario where the user manually refreshes the app or clicks the browser back button, which leads to running outdated URL information from the inspection page.
      // In that case, if the planned or submitted inspection is already added to the indexedDB by the setTakeInChargeStatus() function, the below condition will find the corresponding model from indexedDB and update it instead of creating a duplicate entity.
      if (res.length) {
        for (let i = 0; i < res.length; i++) {
          const modelData = JSON.parse(res[i].model);
          // For planned inspections, we check the planned inspection ID since the inspection is new and does not yet have an inspection ID.
          // For submitted/massive imported inspections, we check the inspection ID since it is an existing inspection.
          // For app imported inspections, we use the referencing inspection ID for the check since it doesn't have an inspection ID generated yet.
          const checkId =
            this.inspectionType === InspectionType.plannedInspection
              ? modelData.plannedInspectionId
              : modelData.id;

          if (checkId === this.recordId) {
            this.idbRecordId = res[i].id;
            this.inspectionType = InspectionType.localInspection;
            this.model = modelData;
            this.loaderService.requestEnded();

            return;
          }
        }
      }

      // Inspection is started for the first time
      this.setTakeInChargeStatus();
    });
  }

  // Moving saved images of parent to corresponding parent in model from storage
  setImages(level: string): void {
    // INFO: Used in template
    const savedImagesList = this.storageService.getSavedFilesListFromStorage();

    switch (level) {
      case BridgeConstants.levels.l0:
        this.model.sharedData.l0Images =
          this.model.sharedData.l0Images.concat(savedImagesList);

        break;

      case BridgeConstants.levels.l1:
        this.model.sharedData.l1Images =
          this.model.sharedData.l1Images.concat(savedImagesList);

        break;

      case BridgeConstants.levels.l2: // Campate List
        this.model.sharedData.l2Images =
          this.model.sharedData.l2Images.concat(savedImagesList);

        break;

      case BridgeConstants.levels.level2:
        this.model.sharedData.level2Images =
          this.model.sharedData.level2Images.concat(savedImagesList);

        break;
    }

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
  }

  setIsLevel2DataChanged(): void {
    // INFO: Used in template
    // Check if any value in the level2Campata.Globale object is in the CdAStatusList
    const isCalculated = Object.values(
      this.model[BridgeConstants.levels.level2Campata].Globale
    ).some((value: any) => this.getCdAStatusList().includes(value));

    // Update the isLevel2DataChanged flag based on the calculated value
    this.isLevel2DataChanged = isCalculated;
  }

  setMaterials(component, field, updateStatus): void {
    // INFO: Used in template
    for (let j = 0; j < field.parent.fieldGroup.length; j++) {
      if (field.parent.fieldGroup[j].key !== 'materiale') continue;

      if (!updateStatus) field.parent.fieldGroup[j].formControl.setValue(null);

      field.parent.fieldGroup[j].props.options = component.materials;

      return;
    }
  }

  async setUniqueCode(level, elementNo, defect): Promise<void> {
    // INFO: Used in template
    this.options.formState[level] = true;
    const direzioneNumber = this.model[level].direzione
      ? `-${DirezioneConfig[this.model[level].direzione].number}`
      : '';
    const componentCountKey = `${this.model[level][elementNo]}-${defect}-${this.model[level].component}${direzioneNumber}`;
    const componentNo = this.getComponentCount(
      level,
      elementNo,
      componentCountKey
    );

    if (this.model[level][elementNo] && this.model[level].component) {
      const component = this.getItemFromListByValue(
        ComponentMaterialMappingObj[defect].component,
        this.model[level].component
      );

      // Condition to avoid hiding of defect table when element no is changed after the table is displayed
      this.options.formState[level] =
        component.materials.length === 0 ? false : !this.model[level].materiale;

      // Updating direzione
      this.model[level]['Scheda Ispezione Ponti di Livello 1'].direzione =
        this.model[level].direzione;

      // Updating unique code
      const direzioneValue = this.model[level].direzione
        ? `.${DirezioneConfig[this.model[level].direzione].number}`
        : '';
      const uniqueCode = `${ComponentMaterialMappingObj[defect].element_type_id}.${this.model[level][elementNo]}.${component.component_type_id}.${componentNo}${direzioneValue}`;

      this.model[level]['Scheda Ispezione Ponti di Livello 1']['Cod unique'] =
        uniqueCode;

      this.model[level]['Scheda Ispezione Ponti di Livello 1']['uniquecode'] =
        uniqueCode;

      // Updating sigla id elem
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Sigla ID Elem'
      ] = `C${this.model[BridgeConstants.levels.l2][BridgeConstants.partNo]}-${
        ComponentMaterialMappingObj[defect].element_code
      }${this.model[level][elementNo]}${this.model[level].direzione ?? ''}-${
        component.component_code
      }${componentNo}`;

      // Updating Codice Elemento
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Codice Elemento'
      ] =
        ComponentMaterialMappingObj[defect].element_code +
        this.model[level][elementNo];

      // Updating Codice Componento
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Codice Componento'
      ] = component.component_code + componentNo;
    }

    // Updating localizzazione
    await this.localizzazioneValue(
      level,
      this.model[level][elementNo],
      defect,
      componentNo
    );
  }

  switchActiveLevel(level: string): void {
    // INFO: Used in template
    this.form.get('activeLevel').setValue(level);
  }

  syncRepeatSections(
    pathsKey: string,
    changedPathNo: string,
    removedIndex?: number
  ): void {
    // INFO: Used in template
    const propPaths = this.model.sharedData[pathsKey];

    propPaths.forEach(({ path, pathNo, syncCondition }) => {
      const shouldSync = syncCondition ? eval(syncCondition) : true;
      if (!shouldSync && pathNo !== changedPathNo) return;

      const pathCopy = this.getDeepCopy(path);
      const initialModel = { ...pathCopy.pop() };
      const repeatSection = this.cubeService.getPropByPathList(
        this.model,
        pathCopy
      );

      if (repeatSection == null) return;

      if (removedIndex == null) {
        repeatSection.push(initialModel);
      } else {
        repeatSection.splice(removedIndex, 1);
      }

      // console.log('path: ', pathCopy, '\nvalue: ', repeatSection);
    });

    this.refreshFields();
  }

  /* // INFO: Tecnico syncing to defect level
  syncRepeatSectionsFromDefectLevels(
    propPaths: any[],
    updatedList: any[]
  ): void {
    // console.log('syncRepeatSectionsFromDefectLevels');
    propPaths.forEach((path) => {
      const pathCopy = this.getDeepCopy(path);
      const initialModel = { ...pathCopy.pop() };
      const repeatSection = this.cubeService.getPropByPathList(this.model, pathCopy);

      if (repeatSection == null) return;

      repeatSection.length = 0;

      updatedList.forEach((updatedItem) => {
        const updatedItemValues = Object.values(updatedItem);
        const initialModelKeys = Object.keys(initialModel);
        const repeatItem = {};

        updatedItemValues.forEach((value, i) => {
          repeatItem[initialModelKeys[i]] = value;
        });

        repeatSection.push(repeatItem);
      });

      // console.log('path: ', pathCopy, '\nvalue: ', repeatSection);
    });
  } */

  syncSharedModelData(
    pathsKey: string,
    updatedValue: any,
    changedIndex?: number
  ): void {
    // INFO: Used in template
    const propPaths = this.model.sharedData[pathsKey];

    propPaths.forEach(({ path, syncCondition }) => {
      const shouldSync = syncCondition ? eval(syncCondition) : true;
      if (!shouldSync) return;

      const pathValue =
        typeof path === 'string' ? eval(path)(changedIndex) : path;
      const pathCopy = [...pathValue];
      const key = pathCopy.pop();
      const parent = this.cubeService.getPropByPathList(this.model, pathCopy);

      if (parent != null && parent[key] != updatedValue) {
        parent[key] = updatedValue;
      }
    });

    this.refreshFields();
  }

  syncSharedModelDataForList(pathsKey: string, updatedValue: any): void {
    // INFO: Used in template
    const propPaths = this.getDeepCopy(this.model.sharedDataForList[pathsKey]);
    propPaths.forEach(({ path, list, subList, syncCondition }) => {
      const shouldSync = syncCondition ? eval(syncCondition) : true;
      if (!shouldSync) return;
      const items = this.cubeService.getPropByPathList(this.model, list);

      items?.forEach((item) => {
        if (subList == null) {
          this.updateSharedValues(updatedValue, item, path);
        } else {
          const subItems = this.cubeService.getPropByPathList(item, subList);

          subItems?.forEach((subItem) => {
            this.updateSharedValues(updatedValue, subItem, path);
          });
        }
      });
    });

    this.refreshFields();
  }

  updateUniqueCode(
    deletedObject,
    defectList: string,
    defect,
    index: number
  ): void {
    // INFO: Used in template
    let componentNo = 1;
    this.model[BridgeConstants.levels.l2][defectList].splice(index, 1);
    this.model[BridgeConstants.levels.l2][defectList] = [].concat(
      this.model[BridgeConstants.levels.l2][defectList]
    );

    const componentCountKey = `${
      deletedObject.modelstructure[`${defect} No`]
    }-${defect}-${deletedObject.modelstructure.component}-${
      deletedObject.modelstructure.direzione
    }`;

    this.model[BridgeConstants.levels.l2].componentCount[componentCountKey] -=
      -1;

    const component = this.getItemFromListByValue(
      ComponentMaterialMappingObj[defect].component,
      deletedObject.modelstructure.component
    );

    for (
      let i = 0;
      i < this.model[BridgeConstants.levels.l2][defectList].length;
      i++
    ) {
      const listItem = this.model[BridgeConstants.levels.l2][defectList][i];
      if (
        listItem.modelstructure.component ===
          deletedObject.modelstructure.component &&
        listItem.modelstructure[`${defect} No`] ===
          deletedObject.modelstructure[`${defect} No`] &&
        listItem.modelstructure.direzione ===
          deletedObject.modelstructure.direzione
      ) {
        // Updating unique code
        const arr = listItem.uniquecode.split('.');
        const uniqueCode =
          arr[0] +
          '.' +
          arr[1] +
          '.' +
          arr[2] +
          '.' +
          componentNo +
          (arr.length > 4 ? `.${arr[4]}` : '');

        listItem.uniquecode = uniqueCode;
        listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Cod unique'
        ] = uniqueCode;
        listItem.modelstructure[
          'Scheda Ispezione Ponti di Livello 1'
        ].uniquecode = uniqueCode;

        // Updating sigla id elem
        listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Sigla ID Elem'
        ] =
          listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
            'Sigla ID Elem'
          ].slice(0, -1) + componentNo;

        // Updating Codice Elemento
        listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'codice elemento'
        ] = `${ComponentMaterialMappingObj[defect].element_code}`;

        // Updating Codice Componento
        listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Component code'
        ] = `${component.component_code}`;

        // Updating localizzazione
        const localizazione = `${listItem.modelstructure.component} - ${component.component_code}${componentNo}`;
        this.model[BridgeConstants.levels.l2][defectList][i].localizazione =
          localizazione;

        listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Localizzazione'
        ] = localizazione;

        // Updating fotoCode
        const direzione = listItem.modelstructure.direzione
          ? '- ' + DirezioneConfig[listItem.modelstructure.direzione].fullName
          : '';

        listItem.modelstructure[
          'Scheda Ispezione Ponti di Livello 1'
        ].fotoCode = `${defect} ${
          listItem.modelstructure[`${defect} No`]
        } ${direzione} (${ComponentMaterialMappingObj[defect].element_code}${
          listItem.modelstructure[`${defect} No`]
        }) - ${listItem.modelstructure.component} (${componentNo})`;

        // Updating codice elemento
        listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Codice Elemento'
        ] =
          ComponentMaterialMappingObj[defect].element_code +
          listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
            'Element No'
          ];

        // Updating codice componento
        listItem.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Codice Componento'
        ] = component.component_code + componentNo;

        componentNo += 1;
      }
    }

    this.refreshFields();
  }

  private calculateDifettositaRelativa(campata: any): any {
    // Combining all defects from every defect sheets in campata
    const combinedDefectsList: any[] = []
      .concat(
        campata[`${BridgeConstants.levels.spalle}list`],
        campata[`${BridgeConstants.levels.pile}list`],
        campata[`${BridgeConstants.levels.archi}list`],
        campata[`${BridgeConstants.levels.impalcati}list`],
        campata[`${BridgeConstants.levels.giunti}list`],
        campata[`${BridgeConstants.levels.apparecchiSismici}list`],
        campata[`${BridgeConstants.levels.fileDiAppoggi}list`]
      )
      .flatMap(
        ({ modelstructure }) =>
          modelstructure['Scheda Ispezione Ponti di Livello 1'][
            'Scheda Ispezione Ponti di Livello 1 List1'
          ]
      );

    // Getting the unique and valid G values (1, 2, 3, 4, 5) from the defects sheet
    const uniqueGValues = [
      ...new Set(
        combinedDefectsList.map((defect) => defect?.G).filter(Boolean)
      ),
    ].sort();

    // Group by G value and calculate difettosita value for each group
    const difettositaRelativa = uniqueGValues.reduce(
      (gValueGroupDifettositaAcc: number, gValue: string) => {
        // Filtering defects based on G value and presence of K1 and K2 values
        const filteredDefectsList: any[] = combinedDefectsList.filter(
          (defect) =>
            defect &&
            defect.G == gValue &&
            defect['Estensione K1'] &&
            defect['Intensità K2']
        );

        // If no defects found, then return gValueGroupDifettositaAcc
        if (filteredDefectsList.length === 0) return gValueGroupDifettositaAcc;

        // Calculating total difettosita value for all the filtered defects
        const filteredDefectsDifettositaTotal: number =
          filteredDefectsList.reduce(
            (filteredDefectsDifettositaAcc: number, defect) => {
              const k1 = Number(defect['Estensione K1']) || 0;
              const k2 = Number(defect['Intensità K2']) || 0;
              const g = Number(defect.G) || 0;

              return filteredDefectsDifettositaAcc + k1 * k2 * g;
            },
            0
          );

        const gValueGroupDifettosita = ['1', '2', '3'].includes(gValue)
          ? filteredDefectsDifettositaTotal / filteredDefectsList.length // Use the average
          : filteredDefectsDifettositaTotal; // Use the total

        return gValueGroupDifettositaAcc + gValueGroupDifettosita;
      },
      0
    );

    // Checking if PS is present in any of the defects
    const presenzaDiPS = combinedDefectsList.some((defect) => defect?.PS);

    return {
      'Difettosita relativa': Number(difettositaRelativa.toFixed(2)) || 0,
      'Presenza di PS': presenzaDiPS,
    };
  }

  private async clearDeletedFilesFromCurrentLevelAndStorage(
    currentModel: any,
    level?: string
  ): Promise<void> {
    delete currentModel.deletedFiles;

    // NEW NOTES: here the deleted images list was also getting concatenated with saved image
    // and then getting deleted from indexdb, but this function is called when back button is
    // clicked meaning deleted images in that case should not be deleted
    // Fetching deleted images
    // const deletedImages = this.storageService.getDeletedFilesListFromStorage();

    // Fetching added images
    let savedFiles = this.storageService
      .getSavedFilesListFromStorage()
      .map((fileURL: string) => ({
        fileName: fileURL,
        isDefect: this.isDefectFile(fileURL),
      }));

    // Concatenate deleted images and added images to prepare for the removal of these files from the indexedDB.
    /* savedFiles = savedFiles.concat(
      this.model.sharedData.l0Images.map((fileURL: string) => ({
        fileName: fileURL,
        isDefect: this.isDefectFile(fileURL),
      }))
    ); */

    const tempTableFiles = await this.getFilesFromTempTable();
    const tempTableKeys = [];

    for (let file of savedFiles) {
      // If deleted file is in indexedDB, first find whether the image is in temp table or original table
      // then deleting file from corresponding indexedDB table
      const fileInTempTable = tempTableFiles.find(
        (tempFile) => tempFile.fileName === file.fileName
      );
      if (fileInTempTable && !fileInTempTable.existingImageStatus) {
        tempTableKeys.push(fileInTempTable.id);
      } else {
        this.idbService.deleteUploadedFile(
          file,
          { modelHash: this.model.modelHash },
          file.isDefect ? StructureType.bridge : null
        );
      }
    }

    if (tempTableKeys.length)
      this.idbService.deleteUploadedFilesFromTempTable(tempTableKeys);

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);

    if (level === BridgeConstants.levels.l0) {
      // Case only happens when user clicks back instead of save as draft in L0. Therefore clearing images in temp table to avoid file residue persist
      this.idbService.clearTempTable(this.model.modelHash);
    } else {
      this.setImagesBackToSavedList(level);
    }
  }

  private convertToPercentage(value: number): string {
    if (value == null || isNaN(value)) return '';

    return Math.round(value * 100) + '%';
  }

  private createDirezioneList(): Record<string, string>[] {
    const direzioneList = Object.entries(DirezioneConfig).map(
      ([key, value]) => ({
        label: value.fullName,
        value: key,
      })
    );

    // Adding blank option
    direzioneList.push({
      label: null,
      value: null,
    });

    return direzioneList;
  }

  private createFormlyOptions(): FormlyFormOptions {
    return {
      formState: {
        l1Descrizione: true,
        l1Frane: true,
        [BridgeConstants.levels.spalle]: true,
        [BridgeConstants.levels.pile]: true,
        [BridgeConstants.levels.archi]: true,
        [BridgeConstants.levels.impalcati]: true,
        [BridgeConstants.levels.giunti]: true,
        [BridgeConstants.levels.apparecchiSismici]: true,
        [BridgeConstants.levels.fileDiAppoggi]: true,
      },
    };
  }

  private createLevelsList(): string[] {
    const levelsList = Object.values(BridgeConstants.levels);

    return [...levelsList];
  }

  private createModel(): any {
    return {
      activeLevel: BridgeConstants.levels.l0,
      fotoCount: 0,
      [BridgeConstants.levels.l0]: {},
      [BridgeConstants.levels.l1]: {},
      modelHash: this.cubeService.generateUniqueId(),
      modelId: '',
      status: InspectionStatus.draft,
      structure_type: StructureType.bridge,
      templateId: `${StructureType.bridge}_${this.templateVersion}`,
    };
  }

  private async deleteAlreadyAddedDefectFiles(level: string): Promise<void> {
    // Called when defect template is reloaded with new defects list

    // Geting defectElementRowIndex
    const defectElementRowIndex =
      this.inspectionService.getDefectRowIndex$Value();

    // Getting files under this defect
    const defectDetails = {
      campataNo:
        this.model[level]['Scheda Ispezione Ponti di Livello 1'][
          BridgeConstants.partNo
        ],
      casoType: -1,
      defectElementListName: `${level}list`,
      defectElementRowIndex,
    };
    /* TODO: Not working need to check; const whereConditions: WhereConditions = {
      modelHash: this.model.modelHash,
      existingImageStatus: false
    }*/
    const tempFileList = await this.getFilesFromTempTable(/*whereConditions*/);
    const defectFilesList = tempFileList.filter((file) => {
      if (file.details) {
        const {
          campataNo,
          casoType,
          defectElementListName,
          defectElementRowIndex,
        } = file.details;

        if (
          campataNo === defectDetails.campataNo &&
          casoType === defectDetails.casoType &&
          defectElementListName === defectDetails.defectElementListName &&
          defectElementRowIndex === defectDetails.defectElementRowIndex
        )
          return file;
      }
    });

    // Adding each file to deleted files list in local storage
    const deletedFilesList = defectFilesList.map((file) => ({
      fileName: file.fileName,
      isDefect: true,
    }));

    // Updating deleted files list in local storage
    this.storageService.addDeletedFileUrlsToStorage(deletedFilesList);
    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
  }

  private deleteDuplicateDataFromModel(): void {
    // Deleting data from model which is not needed to be saved to DB
    // Shared Data
    this.model.sharedData.l0Images = [];
    this.model.sharedData.l1Images = [];
    this.model.sharedData.l2Images = [];
    this.model.sharedData.l3Images = [];
    this.model.sharedData.level2Images = [];

    // Level 1
    const { opereparzialilist } = this.model[BridgeConstants.levels.l0];
    if (!opereparzialilist?.length) return;

    this.model[BridgeConstants.levels.l1] = {};

    // Level 2
    const { campatelist } = opereparzialilist[0].modelstructure;
    if (!campatelist?.length) return;

    this.model[BridgeConstants.levels.l2] = {};

    // Defect levels
    [
      BridgeConstants.levels.spalle,
      BridgeConstants.levels.pile,
      BridgeConstants.levels.archi,
      BridgeConstants.levels.impalcati,
      BridgeConstants.levels.giunti,
      BridgeConstants.levels.apparecchiSismici,
      BridgeConstants.levels.fileDiAppoggi,
    ].forEach((defectLevel) => {
      if (campatelist[0].modelstructure[`${defectLevel}list`].length !== 0) {
        this.model[defectLevel] = {};
      }
    });
  }

  private async deleteUnusedImages(): Promise<void> {
    const tempTableFiles = await this.getFilesFromTempTable();
    const tempTableKeys = [];

    let deletedFilesList = this.storageService.getDeletedFilesListFromStorage();

    deletedFilesList = deletedFilesList.concat(
      this.model[BridgeConstants.levels.l0].deletedFiles ?? []
    );

    const alreadyUploadedFilesList = [];
    deletedFilesList.forEach((deletedFile) => {
      // If deleted file was already uploaded
      if (this.cubeService.isAlreadyUploadedFile(deletedFile.fileName)) {
        alreadyUploadedFilesList.push(deletedFile.fileName);

        return;
      }

      // If deleted file is in indexedDB, first find whether the image is in temp table or original table
      // then deleting file from corresponding indexedDB table
      const fileInTempTable = tempTableFiles.find(
        (file) => file.fileName === deletedFile.fileName
      );
      if (fileInTempTable && !fileInTempTable.existingImageStatus) {
        tempTableKeys.push(fileInTempTable.id);
      } else {
        this.idbService.deleteUploadedFile(
          deletedFile,
          { modelHash: this.model.modelHash },
          deletedFile.isDefect ? StructureType.bridge : null
        );
      }

      const fileAlreadyInListIndex =
        this.repeatSectionImageListForModification.findIndex(
          ({ fileName }) => fileName === deletedFile.fileName
        );
      const defectFileAlreadyInListIndex =
        this.defectImageListForModification.findIndex(
          ({ fileName }) => fileName === deletedFile.fileName
        );
      if (fileAlreadyInListIndex !== -1)
        this.repeatSectionImageListForModification.splice(
          fileAlreadyInListIndex,
          1
        );

      if (defectFileAlreadyInListIndex !== -1)
        this.defectImageListForModification.splice(
          defectFileAlreadyInListIndex,
          1
        );
    });

    if (tempTableKeys.length)
      this.idbService.deleteUploadedFilesFromTempTable(tempTableKeys);

    if (alreadyUploadedFilesList.length) {
      // Deleting files from DB
      await this.inspectionService
        .deleteUploadedFiles(alreadyUploadedFilesList)
        .toPromise();
    }

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);
    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
    delete this.model[BridgeConstants.levels.l0].deletedFiles;
  }

  /* // INFO: Commenting as per RINA-6309
  private async duplicateIndexedDbFiles(
    newValue: number,
    currentValue: number,
    isDuplicateDefect: boolean,
    originalFotoCount: number,
    listName: string = '',
    campataNo: number = 0
  ) {
    let whereConditions: WhereConditions = {
      campataNo: currentValue,
      modelHash: this.model.modelHash,
    };
    if (isDuplicateDefect) {
      whereConditions = {
        ...whereConditions,
        campataNo,
        casoType: -1,
        componentListName: listName,
        componentRowIndex: currentValue,
      };
    }

    const defectFilesList = await this.idbService.getUploadedFiles(
      whereConditions,
      StructureType.bridge,
    );
    const neededFileNameParts = [
      BridgeConstants.defectElements.spalle,
      BridgeConstants.defectElements.pile,
      BridgeConstants.defectElements.archi,
      BridgeConstants.defectElements.impalcati,
      BridgeConstants.defectElements.giunti,
      BridgeConstants.defectElements.apparecchiSismici,
      BridgeConstants.defectElements.fileDiAppoggi,
      BridgeConstants.eventualiNote,
    ];
    let fotoCount = originalFotoCount;
    const newList = defectFilesList.map((defectFile) => {
      // INFO: Similar functionality is also used in duplicateDefect() and in duplicate-campata-dialog.component.ts file
      if (isDuplicateDefect) {
        defectFile.componentRowIndex = newValue;
      } else {
        defectFile.campataNo = newValue;
      }

      delete defectFile.id;
      const nameParts = defectFile.fotoName.split(' ');
      // Checking if defect is not elementi accessori defect
      const isNotElementiAccessoriDefect = neededFileNameParts.some(
        (neededFileNamePart) => defectFile.fotoName.includes(neededFileNamePart)
      );

      if (isNotElementiAccessoriDefect) {
        // Updating foto count
        fotoCount++;
        nameParts[1] = fotoCount;
        defectFile.fotoName = nameParts.join(' ');
      }

      return defectFile;
    });

    await this.idbService.addUploadedFiles(newList);
  } */

  private async fetchCasoImagesAndAddToDeleteList(
    fileName: string
  ): Promise<void> {
    const whereConditions: WhereConditions = {
      modelHash: this.model.modelHash,
      fileName,
    };

    const defectFilesList = await this.idbService.getFilesFromTempTable(
      whereConditions
    );

    // Adding each file to deleted files list in local storage
    const deletedFilesList = defectFilesList.map((file) => ({
      fileName,
      isDefect: true,
    }));

    // Updating deleted files list in local storage
    this.storageService.addDeletedFileUrlsToStorage(deletedFilesList);
  }

  private fetchDefectsList({
    level,
    ...defectSearchParams
  }: {
    level: string;
  } & DefectSearchParams): void {
    this.inspectionService
      .getBridgeDefects(defectSearchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          if (res.data.length) {
            this.form = new FormGroup({});
            switch (level) {
              case 'tipologiaElementiStructures':
                this.setLevel1TipologiaElementiStructures(res.data[0]);
                break;

              case BridgeConstants.elementiAccessori:
                this.setElementiAccessoriDefects(
                  res.data[0][BridgeConstants.elementiAccessori]
                );
                break;

              default:
                // Other defects
                this.setDefects(res.data, level);
                this.deleteAlreadyAddedDefectFiles(level);
                break;
            }
          }
        },
        (error) => {
          this.fetchDefectsListFromIndexedDb({ level, ...defectSearchParams });
        }
      );
  }

  private async fetchDefectsListFromIndexedDb({
    level,
    ...defectSearchParams
  }: {
    level: string;
  } & DefectSearchParams): Promise<void> {
    const defectsList = await this.idbService.getBridgeDefectsList(
      defectSearchParams
    );

    this.form = new FormGroup({});
    if (level === 'tipologiaElementiStructures') {
      // L1 > Tipologia elementi strutturali
      this.setLevel1TipologiaElementiStructures(defectsList[0]);
    } else if (level === 'Elementi Accessori') {
      // Elementi Accessori Defects
      this.setElementiAccessoriDefects(defectsList[0]['Elementi Accessori']);
    } else {
      // Defects
      this.deleteAlreadyAddedDefectFiles(level);
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ] = defectsList;
      this.options.formState[level] = false;
    }
  }

  private formatCdaClass(value: string): string {
    if (typeof value !== 'string') return '-';

    return this.getCdAStatusList().some(
      (status) => status?.toLowerCase() === value?.toLowerCase()
    )
      ? value
      : '-';
  }

  private formatValue(value: any): any {
    if (this.hasValue(value)) return value;

    return null;
  }

  private getCampataAttentionClassFormData(): Record<string, any> {
    const {
      [BridgeConstants.level2RiskSheets.strutturaleEFondazionale]:
        strutturaleEFondazionale,
      [BridgeConstants.level2RiskSheets.sismica]: sismica,
      [BridgeConstants.level2RiskSheets.frane]: frane,
      [BridgeConstants.level2RiskSheets.idraulica]: idraulica,
    } = this.model[BridgeConstants.levels.level2Campata];
    const formData: Record<string, any> = {};

    // Strutturale e fondazionale
    if (strutturaleEFondazionale.isRiskSheetFilled) {
      const pericolosita =
        strutturaleEFondazionale['Stima del livello di pericolosita'];
      const vulnerabilita =
        strutturaleEFondazionale['Stima del livello di vulnerabilita'];
      const esposizione =
        strutturaleEFondazionale['Stima del livello di esposizione'];

      formData[BridgeConstants.level2RiskSheets.strutturaleEFondazionale] = {
        classificazione_strada: pericolosita['Classificazione strada'],
        veicoli_commerciali:
          pericolosita['Traffico Medio Giornaliero Veicoli commerciali'],
        difettosita_relativa: Number(vulnerabilita['Difettosita relativa']),
        presenza_di_ps: vulnerabilita['Presenza di PS'],
        anno_di_costruzione: vulnerabilita['Anno di costruzione'],
        norma_di_progetazione: vulnerabilita['Norma di progetazione'],
        schema_statico: vulnerabilita['Schema statico'],
        materiale: vulnerabilita['Materiale'],
        luce_massima_campata_m: Number(vulnerabilita['Luce massima campata m']),
        n_di_campate: vulnerabilita['N° di campate'],
        traffico_medio_giornaliero: esposizione?.['Traffico medio giornaliero'],
        luce_media_della_campata: Number(
          esposizione?.['Luce media della campata']
        ),
        alternative_stradali: esposizione?.['Alternative stradali'],
        tipologia_ente_scavalcato: esposizione?.['Tipologia Ente scavalcato'],
        trasporto_di_merci_pericolose:
          esposizione?.['Transporto di merci pericolose'],
      };

      if (strutturaleEFondazionale.isUserModifiedLivelloDiDifettosita) {
        formData[
          BridgeConstants.level2RiskSheets.strutturaleEFondazionale
        ].livello_di_difettosita = vulnerabilita['Livello di difettosita'];
      }
    }

    // Sismica
    if (sismica.isRiskSheetFilled) {
      const pericolosita =
        sismica['Valutazione del livello di pericolosità sismica'];
      const vulnerabilita =
        sismica['Stima del livello di vulnerabilità sismica'];
      const esposizione = sismica['Stima del livello di esposizione sismica'];

      formData[BridgeConstants.level2RiskSheets.sismica] = {
        accelerazione: pericolosita['Accelerazione di picco al suolo'],
        topografica: pericolosita['Categoria topografica'],
        sottosuolo: pericolosita['Categoria sottosuolo'],
        materiale: vulnerabilita['Materiale'],
        luce_massima_campata_m: Number(vulnerabilita['Luce massima campata m']),
        campate: vulnerabilita['Campate'],
        schema_statico: vulnerabilita['Schema statico'],
        vulnerabilit_sismica:
          vulnerabilita['Elementi di vulnerabilità sismica'],
        progettazione: vulnerabilita['Criteri di progettazione'],
        strategita_dell_opera: esposizione?.["Strategità dell'opera"],
      };

      if (sismica.isUserModifiedLivelloDiDifettosita) {
        formData[
          BridgeConstants.level2RiskSheets.sismica
        ].livello_di_difettosita = vulnerabilita['Livello di difettosità'];
      }
    }

    // Frane
    if (frane.isRiskSheetFilled) {
      const suscettibilita =
        frane['Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'];
      const vulnerabilita =
        frane['Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane'];
      const isNegligible = frane['Fenomeno trascurabile'] === 'SI';

      if (isNegligible) {
        formData[BridgeConstants.level2RiskSheets.frane] = {
          fenomeno_trascurabile: frane['Fenomeno trascurabile'],
        };
      } else {
        formData[BridgeConstants.level2RiskSheets.frane] = {
          fenomeno_trascurabile: frane['Fenomeno trascurabile'],
          instabilita: suscettibilita['Instabilità del versante'],
          stato_di_attivita: suscettibilita['Stato di attività'],
          massima_velocita: suscettibilita['Massima velocità attesa'],
          magnitudo_m3:
            suscettibilita['Magnitudo attesa su base volumetrica in m3'],
          affidabilita: suscettibilita['Affidabilità della valutazione'],
          misure_mitigazione: suscettibilita['Misure di mitigazione'],
          estensione_nterferenza: vulnerabilita['Estensione Interferenza'],
          presenza_orizzontali:
            vulnerabilita[
              'Presenza di fondazioni superficiali o comunque NON progettate per resistere alle azioni orizzontali'
            ],
        };
      }
    }

    // Idraulica
    if (idraulica.isRiskSheetFilled) {
      const suscettibilita =
        idraulica['Stima della suscettibilita legeta al rischio idraulico'];
      const vulnerabilita =
        idraulica['Stima della vulnerabilita legeta al rischio idraulico'];
      const isNegligible = idraulica['Fenomeno trascurabile'] === 'SI';

      if (isNegligible) {
        formData[BridgeConstants.level2RiskSheets.idraulica] = {
          fenomeno_trascurabile: idraulica['Fenomeno trascurabile'],
        };
      } else {
        formData[BridgeConstants.level2RiskSheets.idraulica] = {
          fenomeno_trascurabile: idraulica['Fenomeno trascurabile'],
          corso_d_acqua: suscettibilita['Corso d acqua'],
          franco_idraulico_atteso: suscettibilita['Franco idraulico atteso'],
          wa_l_larghezza_alveo_monte_ponte: this.formatValue(
            Number(suscettibilita['wa l Larghezza alveo a monte del ponte'])
          ),
          wa_larghezza_alveo_ponte: this.formatValue(
            Number(
              suscettibilita[
                'wa Larghezza complessiva alveo in corrispondenza del ponte'
              ]
            )
          ),
          wg_l_larghezza_golene_rilevati_accesso: this.formatValue(
            Number(
              suscettibilita[
                'Wg l Larghezza complessiva delle golene occupata dai rilevati di accesso'
              ]
            )
          ),
          wg_larghezza_golene_monte_ponte: this.formatValue(
            Number(
              suscettibilita[
                'Wg Larghezza complessiva delle golene a monte del ponte'
              ]
            )
          ),
          a_diametro_pila: this.formatValue(
            Number(suscettibilita['a Diametro pila'])
          ),
          ds_profondita_scavo_max: this.formatValue(
            Number(suscettibilita['ds Massima profondità di scavo'])
          ),
          df_profondita_fondazione_alveo: this.formatValue(
            Number(
              suscettibilita[
                'df Profondità di posa del piano di fondazione rispetto all alveo'
              ]
            )
          ),
          dim_bacino_idrografico_km2: this.formatValue(
            vulnerabilita['Dimensioni del bacino idrografico km2']
          ),
          fenomeni_deposizione: vulnerabilita['Fenomeni di deposizione'],
          trasporto_materiale_vegetale:
            vulnerabilita['Transporto di materiale vegetale'],
          evidenza_di_presenza_fondazioni:
            vulnerabilita[
              'Evidenza di presenza di fondazioni superficiali delle pile e delle spalle del ponte'
            ],
          evidenza_di_fenomeni_27:
            vulnerabilita[
              'Evidenza di fenomeni di abbassamento generalizzato dell alveo a monte e a valle del ponte 1'
            ],
          ponte_posizionato:
            vulnerabilita[
              'Ponte posizionato in tratto di alveo avente sensibile curvatura'
            ],
          evidenza_di_fenomeni_30:
            vulnerabilita[
              'Evidenza di fenomeni di abbassamento generalizzato dell alveo a monte e a valle del ponte 2'
            ],
          presenza_di_accumuli:
            vulnerabilita[
              'Presenza di accumuli di detriti o materiale flottante a monte della pila'
            ],
          tendenza_dell_alveo:
            vulnerabilita['Tendenza dell alveo alla divagazione planimetrica'],
          evidenza_di_presenza_protezione:
            vulnerabilita[
              'Evidenza di presenza di protezione al piede delle pile e delle spalle del ponte'
            ],
          presenza_di_una_briglia:
            vulnerabilita[
              'Presenza di una briglia di protezione immediatamente a valle del ponte'
            ],
        };
      }
    }

    return formData;
  }

  private generateCampateNoList(): void {
    const campateNo =
      this.model[BridgeConstants.levels.l0]['Caratteristiche geometriche'][
        'N° campate'
      ];
    this.campateNoList = Array(campateNo)
      .fill(0)
      .map((x, i) => ({
        value: i + 1,
        label: i + 1,
      }));
  }

  private generateTemplate(): void {
    this.loaderService.requestStarted();
    switch (this.inspectionType) {
      case InspectionType.newInspection:
        this.getTemplateByVersion(this.templateVersion);

        return;

      case InspectionType.plannedInspection:
        this.plannedInspectionId = this.recordId;
        this.getPlannedInspection(this.plannedInspectionId);

        return;

      case InspectionType.appImportedInspection:
        this.appImportedInspectionId = this.recordId;
        this.importInspection(this.appImportedInspectionId);

        return;

      case InspectionType.localInspection:
        this.idbRecordId = this.recordId;
        from(this.idbService.getInspectionModel(parseInt(this.idbRecordId)))
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            this.model = JSON.parse(res.model);
            this.setImagesToTempTable();
            this.getImageFromIndexedDb();
            this.generateCampateNoList();
          });

        return;

      case InspectionType.submittedInspection:
        this.inspectionId = this.recordId;
        this.getSubmittedInspection(this.inspectionId);

        return;

      case InspectionType.massiveImportedInspection:
        this.inspectionId = this.recordId;
        this.getMassiveImportedInspection(this.inspectionId);

        return;

      default:
        break;
    }
  }

  private getCdAStatusList(): string[] {
    return [
      CdAStatus.alta,
      CdAStatus.medioAlta,
      CdAStatus.media,
      CdAStatus.medioBassa,
      CdAStatus.bassa,
    ];
  }

  private getComponentCount(level, elementNo, componentCountKey): number {
    let componentNo = 1;
    if (
      this.model[level].component &&
      this.model[BridgeConstants.levels.l2].componentCount &&
      this.model[BridgeConstants.levels.l2].componentCount[componentCountKey]
    ) {
      componentNo =
        this.model[BridgeConstants.levels.l2].componentCount[componentCountKey];

      if (
        this.model[level].component !== this.l3UpdateComponent ||
        this.model[level][elementNo] !== this.l3UpdateElementNo ||
        this.model[level].direzione !== this.l3UpdateDirezione
      ) {
        componentNo += 1;
      }
    }
    return componentNo;
  }

  private getElementCount(list: any[], elementType): number {
    const set = new Set();
    list.forEach((x) => set.add(x.modelstructure[elementType]));

    return set.size;
  }

  private async getImageFromIndexedDb(): Promise<void> {
    const multiImagesList = await this.idbService.getUploadedFilesList({
      imageType: 2,
      modelHash: this.model.modelHash,
    });
    const fileList = multiImagesList.map((image) => image.file);

    this.model[BridgeConstants.levels.l0].MediaList.mediaFile = fileList;
    this.getTemplateByVersion(this.templateVersion);
  }

  private getInspectionMediaToken(): Promise<void> {
    return this.inspectionService
      .getInspectionMediaToken()
      .pipe(
        map((res) => {
          this.token = res.data.token;
          this.storageService.addToStorage(
            STORAGE_KEYS.inspectionToken,
            res.data.token
          );
        }),
        takeUntil(this.destroy$)
      )
      .toPromise();
  }

  private getLevel1DataForAttentionClass(model): any {
    const caratteristicheAlveo =
      model['Rischio idraulico']['Caratteristiche Alveo'];
    const tipologiaDiAlveo = model['Rischio idraulico']['Tipologia di alveo'];

    const indicareEventuale =
      caratteristicheAlveo[
        'Indicare l’eventuale sussistenza delle seguenti condizioni'
      ];

    const campateList = (model?.campatelist ?? []).map((campata) => {
      return {
        campataNo: campata['Campata No'],
        difettositaRelativaCalculation:
          campata?.modelstructure?.difettositaRelativaCalculation,
      };
    });

    return {
      [BridgeConstants.level2RiskSheets.sismica]: {
        SchemaIsostatico: model['Tipologia strutturale']['Schema isostatico'],
        SchemaIperstatico: model['Tipologia strutturale']['Schema iperstatico'],
        ElementiDiVulnerabilitaSismica:
          model['Elementi di vulnerabilità sismica'][
            'Elementi di vulnerabilità sismica vedi 4 3 3 delle Linee Guida'
          ]['Assenti/Presenti'],
      },
      [BridgeConstants.level2RiskSheets.frane]: {
        instabilitaDelVersante:
          model['Rischio frane'][
            'Parametro dello Stato di Attività per la quantificazione di PA frane riconosciute / potenziali'
          ][
            'Parametro dello Stato di Attività per la quantificazione di PA frane riconosciute / potenziali - radio'
          ],
        massimaVelocitaAttesa:
          model['Rischio frane'][
            'Parametro della massima velocità attesa in funzione della tipologia di frana in atto o potenziale PV - radio'
          ],
        MagnitudoAttesa:
          model['Rischio frane'][
            'Parametro della magnitudo su base volumetrica in metri cubi PM frane riconosciute / potenziali - radio'
          ],
        affidabilitaDellaValutazione:
          model['Rischio frane']['Affidabilità complessiva della valutazione'][
            'Affidabilità complessiva della valutazione - radio'
          ],
      },
      [this.bridgeConstants.level2RiskSheets.idraulica]: {
        CorsodAcqua: {
          ReticoloPrincipale:
            model['Rischio idraulico']['Tipologia di reticolo'][
              'Reticolo principale'
            ],
          ReticoloSecondario:
            model['Rischio idraulico']['Tipologia di reticolo'][
              'Reticolo secondario'
            ],
          ReticoloArtificialeDiScolo:
            model['Rischio idraulico']['Tipologia di reticolo'][
              'Reticolo artificiale di scolo'
            ],
        },
        larghezzaAlveo:
          tipologiaDiAlveo[
            'Larghezza complessiva dell’alveo inciso occupata dall’ingombro di pile e spalle wa,l'
          ],
        larghezzaComplessivaAlveo:
          tipologiaDiAlveo[
            'Larghezza complessiva dell’alveo inciso a monte del ponte wa'
          ],
        larghezzaComplessivaDelle:
          tipologiaDiAlveo[
            'Larghezza complessiva delle golene occupata dai rilevati di accesso, dalle spalle e dalle pile wg,l'
          ],
        larghezzaComplessivaDelleGolene:
          tipologiaDiAlveo[
            'Larghezza complessiva delle golene a monte del ponte wg'
          ],
        massimaProfonditaDiScavo:
          caratteristicheAlveo['Massima profondità di scavo ds'],
        profonditaDiPosaDelPiano:
          caratteristicheAlveo[
            'Profondità di posa del piano di fondazione rispetto all’alveo df'
          ],
        indicareEventuale: {
          'Evidenza di presenza di fondazioni superficiali delle pile e delle spalle del ponte':
            indicareEventuale[
              'Evidenza di presenza di fondazioni superficiali delle pile e delle spalle del ponte'
            ],
          'Evidenza di fenomeni di abbassamento generalizzato dell’alveo a monte e a valle del ponte':
            indicareEventuale[
              'Evidenza di fenomeni di abbassamento generalizzato dell’alveo a monte e a valle del ponte'
            ],
          'Ponte posizionato in tratto di alveo avente sensibile curvatura':
            indicareEventuale[
              'Ponte posizionato in tratto di alveo avente sensibile curvatura'
            ],
          'Presenza di accumuli di detriti o materiale flottante a monte della pila':
            indicareEventuale[
              'Presenza di accumuli di detriti o materiale flottante a monte della pila'
            ],
          'Tendenza dell’alveo alla divagazione planimetrica':
            indicareEventuale[
              'Tendenza dell’alveo alla divagazione planimetrica'
            ],
          'Evidenza di presenza di protezione al piede delle pile e delle spalle del ponte':
            indicareEventuale[
              'Evidenza di presenza di protezione al piede delle pile e delle spalle del ponte'
            ],
          'Presenza di una briglia di protezione immediatamente a valle del ponte':
            indicareEventuale[
              'Presenza di una briglia di protezione immediatamente a valle del ponte'
            ],
        },
      },
      campateList,
    };
  }

  private getMassiveImportedInspection(inspectionId: string): void {
    this.inspectionService
      .getInspection(inspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.massiveImportedInspection = res.data;
        this.model.status = InspectionStatus.takeInCharge;
        this.model.inspectionRecordId = inspectionId;
        this.model.id = inspectionId;
        this.model.modelHash = this.massiveImportedInspection.modelHash;
        this.getTemplateByVersion(this.templateVersion);
      });
  }

  private getPeriodoFromInspectionDate(): string {
    // Getting the starting date of the first date in Level 1
    const periodo =
      this.model[BridgeConstants.levels.l0].opereparzialilist?.[0]
        ?.modelstructure['Schede descrittive di ispezione ponti di Livello 1'][
        'Schede descrittive di ispezione ponti di Livello 1 section'
      ][0]['Data ispezione'] ?? '';

    if (Array.isArray(periodo)) {
      // Getting the starting date of the first date in Level 1
      return periodo.length > 0 ? periodo[0] : '';
    }

    return periodo;
  }

  private getPlannedInspection(plannedInspectionId: string): void {
    this.inspectionCrudService
      .getPlannedInspection(plannedInspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.plannedInspection = res.data;
        this.model.plannedInspectionId = this.plannedInspectionId;
        this.getTemplateByVersion(this.templateVersion);
      });
  }

  private getRiskSheetDataForCopying(sheetData: any, sheetName: string): any {
    switch (sheetName) {
      case BridgeConstants.level2RiskSheets.strutturaleEFondazionale: {
        const pericolosita = sheetData['Stima del livello di pericolosita'];
        const vulnerabilita = sheetData['Stima del livello di vulnerabilita'];
        const esposizione = sheetData['Stima del livello di esposizione'];

        return {
          'Stima del livello di pericolosita': {
            'Classificazione strada': pericolosita['Classificazione strada'],
            'Traffico Medio Giornaliero Veicoli commerciali':
              pericolosita['Traffico Medio Giornaliero Veicoli commerciali'],
          },
          'Stima del livello di vulnerabilita': {
            'Anno di costruzione': vulnerabilita['Anno di costruzione'],
            'Norma di progetazione': vulnerabilita['Norma di progetazione'],
            'Schema statico': vulnerabilita['Schema statico'],
            Materiale: vulnerabilita['Materiale'],
          },
          'Stima del livello di esposizione': {
            'Traffico medio giornaliero':
              esposizione?.['Traffico medio giornaliero'],
            'Tipologia Ente scavalcato':
              esposizione?.['Tipologia Ente scavalcato'],
            'Transporto di merci pericolose':
              esposizione?.['Transporto di merci pericolose'],
          },
        };
      }

      case BridgeConstants.level2RiskSheets.sismica: {
        const pericolosita =
          sheetData['Valutazione del livello di pericolosità sismica'];
        const vulnerabilita =
          sheetData['Stima del livello di vulnerabilità sismica'];
        const esposizione =
          sheetData['Stima del livello di esposizione sismica'];

        return {
          'Valutazione del livello di pericolosità sismica': {
            'Categoria topografica': pericolosita['Categoria topografica'],
            'Categoria sottosuolo': pericolosita['Categoria sottosuolo'],
          },
          'Stima del livello di vulnerabilità sismica': {
            Materiale: vulnerabilita['Materiale'],
            Campate: vulnerabilita['Campate'],
            'Criteri di progettazione':
              vulnerabilita['Criteri di progettazione'],
          },
          'Stima del livello di esposizione sismica': {
            "Strategità dell'opera": esposizione?.["Strategità dell'opera"],
          },
        };
      }

      case BridgeConstants.level2RiskSheets.frane: {
        const suscettibilita =
          sheetData[
            'Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'
          ];
        const vulnerabilita =
          sheetData[
            'Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane'
          ];

        return {
          'Fenomeno trascurabile': sheetData['Fenomeno trascurabile'],
          'Stima Del Livello Di Suscettibilità Legato Al Rischio Frane': {
            'Stato di attività': suscettibilita['Stato di attività'],
            'Massima velocità attesa':
              suscettibilita['Massima velocità attesa'],
            'Magnitudo attesa su base volumetrica in m3':
              suscettibilita['Magnitudo attesa su base volumetrica in m3'],
            'Misure di mitigazione': suscettibilita['Misure di mitigazione'],
          },
          'Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane': {
            'Estensione Interferenza': vulnerabilita['Estensione Interferenza'],
            'Presenza di fondazioni superficiali o comunque NON progettate per resistere alle azioni orizzontali':
              vulnerabilita[
                'Presenza di fondazioni superficiali o comunque NON progettate per resistere alle azioni orizzontali'
              ],
          },
        };
      }

      case BridgeConstants.level2RiskSheets.idraulica: {
        const suscettibilita =
          sheetData['Stima della suscettibilita legeta al rischio idraulico'];
        const vulnerabilita =
          sheetData['Stima della vulnerabilita legeta al rischio idraulico'];

        return {
          'Fenomeno trascurabile': sheetData['Fenomeno trascurabile'],
          'Stima della suscettibilita legeta al rischio idraulico': {
            'Franco idraulico atteso':
              suscettibilita['Franco idraulico atteso'],
            'a Diametro pila': suscettibilita['a Diametro pila'],
          },
          'Stima della vulnerabilita legeta al rischio idraulico': {
            'Fenomeni di deposizione': vulnerabilita['Fenomeni di deposizione'],
            'Transporto di materiale vegetale':
              vulnerabilita['Transporto di materiale vegetale'],
          },
        };
      }
    }
  }

  private getRiskSheetDefaultValues(riskSheet: string): any {
    switch (riskSheet) {
      case BridgeConstants.level2RiskSheets.strutturaleEFondazionale:
        return {
          [this.bridgeConstants.level2RiskSheets.strutturaleEFondazionale]: {
            'Stima del livello di pericolosita': {
              'Stima del livello di pericolosita calculated': null,
              'Classificazione strada': null,
              'Traffico Medio Giornaliero Veicoli commerciali': null,
              'Classe di pericolosita': null,
            },
            'Stima del livello di vulnerabilita': {
              'Livello di difettosita': null,
              'Stima del livello di vulnerabilita calculated': null,
              'Anno di costruzione': null,
              'Norma di progetazione': null,
              'Schema statico': null,
              Materiale: null,
              'Classe di vulnerabilita': null,
              'Luce massima campata m': null,
            },
            'Stima del livello di esposizione': {
              'Luce media della campata calculated': null,
              'Stima del livello di esposizione calculated': null,
              'Traffico medio giornaliero': null,
              'Tipologia Ente scavalcato': null,
              'Transporto di merci pericolose': null,
              'Classe di esposizione': null,
              'Luce media della campata': null,
            },
            'Stima della classe di attenzione strutturale e fondazionale': {
              'Classe di attenzione': null,
            },
            isRiskSheetFilled: false,
          },
        };
      case BridgeConstants.level2RiskSheets.sismica:
        return {
          [this.bridgeConstants.level2RiskSheets.sismica]: {
            'Valutazione del livello di pericolosità sismica': {
              'Categoria topografica calculated': null,
              'Categoria sottosuolo calculated': null,
              'Categoria topografica': null,
              'Categoria sottosuolo': null,
              'Classe di Pericolosita': null,
            },
            'Stima del livello di vulnerabilità sismica': {
              'Luce massima campata m select': null,
              Materiale: null,
              Campate: null,
              'Criteri di progettazione': null,
              'Classe di vulnerabilita': null,
              'Livello di difettosità': null,
              'Luce massima campata m': null,
            },
            'Stima del livello di esposizione sismica': {
              "Strategità dell'opera": null,
              'Classe di esposizione': null,
            },
            'Stima della classe di attenzione sismica a livello territoriale': {
              'Classe di attenzione': null,
            },
            isRiskSheetFilled: false,
          },
        };

      case BridgeConstants.level2RiskSheets.frane:
        return {
          [this.bridgeConstants.level2RiskSheets.frane]: {
            'Fenomeno trascurabile': null,
            'Stima Del Livello Di Suscettibilità Legato Al Rischio Frane': {
              'Massima velocità attesa': null,
              'Magnitudo attesa su base volumetrica in m3': null,
              'Massima velocità attesa value': null,
              'Magnitudo attesa su base volumetrica in m3 value': null,
              'Stato di attività': null,
              'Misure di mitigazione': null,
              'Classe Di Suscettibilita': null,
            },
            'Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane': {
              'Estensione Interferenza': null,
              'Presenza di fondazioni superficiali o comunque NON progettate per resistere alle azioni orizzontali':
                null,
              'Classe Di Vulnerabilita': null,
            },
            'Stima Del Livello Di Esposizione Legato Al Rischio Frane': {
              'Classe Di Esposizione Rischio Frane': null,
            },
            'Stima Della Classe Di Attenzione Frane A Livello Territoriale': {
              'Classe Di Attenzione': null,
            },
            isRiskSheetFilled: false,
          },
        };

      case BridgeConstants.level2RiskSheets.idraulica:
        return {
          [this.bridgeConstants.level2RiskSheets.idraulica]: {
            'Fenomeno trascurabile': null,
            'Stima della suscettibilita legeta al rischio idraulico': {
              'a Diametro pila': null,
              'Franco idraulico atteso': null,
              'Classe di suscettibilita per il fenomeno di sormonto': null,
              'ca Fattore di restringimento': null,
              'Cg fattore restringimento delle aree golenali': null,
              'Classe di suscettibilita per il fenomeno di erosione generalizzata':
                null,
              IEL: null,
              'Classe di suscettibilita per il fenomeno di erosione localizzata':
                null,
            },
            'Stima della vulnerabilita legeta al rischio idraulico': {
              'Fenomeni di deposizione': null,
              'Transporto di materiale vegetale': null,
              'Classe di vulnerabilita per il fenomeno di sormonto': null,
              'Classe di vulnerabilita per il fenomeno di erosione generalizzata':
                null,
              'Classe di vulnerabilita per il fenomeno di erosione localizzata':
                null,
              'Dimensioni del bacino idrografico km2': null,
            },
            'Stima dell espezione legata al rischio idraulico': {
              'Classe di esposizione': null,
            },
            'Stima della classe di attenzione rischio idraulico': {
              'Classe di attenzione rischio sormonto': null,
              'Classe di attenzione rischio erosione generalizzata': null,
              'Classe di attenzione rischio erosione localizzata': null,
              'Classe di attenzione rischio erosione': null,
              'Classe di attenzione rischio idraulico': null,
            },
            isRiskSheetFilled: false,
          },
        };

      case BridgeConstants.level2RiskSheets.globale:
        return {
          [this.bridgeConstants.level2RiskSheets.globale]: {
            'Classe Di Attenzione Strutturale Fondazionale': null,
            'Classe Di Attenzione Sismica': null,
            'Classe Di Attenzione Frane': null,
            'Classe Di Attenzione Idraulica': null,
            'Classe Di Attenzione Globale Della Campata': null,
          },
        };
    }
  }

  private getSubmittedInspection(inspectionId: string): void {
    this.inspectionService
      .getInspection(inspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.model = res.data;
        this.model.status = InspectionStatus.takeInCharge;
        this.getTemplateByVersion(this.templateVersion);
        this.generateCampateNoList();
      });
  }

  private async getTemplateByVersion(
    templateVersion: BridgeTemplateVersion
  ): Promise<void> {
    const template = await this.templateLoaderService.loadBridgeTemplate(
      templateVersion,
      {
        component: this,
        isDisabled: this.isDisabled,
      }
    );
    this.setTemplate(template);
  }

  private async getFilesFromTempTable(
    additionalWhereConditions?: WhereConditions
  ): Promise<any[]> {
    const whereConditions: WhereConditions = {
      modelHash: this.model.modelHash,
      ...additionalWhereConditions,
    };
    const imageList = await this.idbService.getFilesFromTempTable(
      whereConditions
    );

    return imageList;
  }

  private getWorstCampata(campateList: any[]): any {
    // Get CdA status list
    const cdaStatusList = this.getCdAStatusList();

    // Sort campate by CdA status
    const sortedCampataByCdaOrder = campateList.sort(
      (a, b) => cdaStatusList.indexOf(a.CdA) - cdaStatusList.indexOf(b.CdA)
    );

    // Filter worst campate by CdA
    const worstCampateListFilteredByCda = sortedCampataByCdaOrder.filter(
      (campata) => {
        return campata.CdA === sortedCampataByCdaOrder[0].CdA;
      }
    );

    // If no campata found, return null
    if (worstCampateListFilteredByCda.length === 0) return null;

    // If only one campata found, return first element from worstCampateListFilteredByCda
    if (worstCampateListFilteredByCda.length === 1)
      return worstCampateListFilteredByCda[0];

    // Sort worst campate by Fondazionale order
    //if there are several spans with the worst Attention Class (high),
    // it must be considered the span with the worst Attention Class of the STRUTTURALE E FONDAZIONALE risk.
    const sortedWorstCampateListByFondazionaleOrder =
      worstCampateListFilteredByCda.sort((a, b) => {
        return (
          cdaStatusList.indexOf(
            a[BridgeConstants.level2RiskSheets.strutturaleEFondazionale]
          ) -
          cdaStatusList.indexOf(
            b[BridgeConstants.level2RiskSheets.strutturaleEFondazionale]
          )
        );
      });

    // If only one campata found, return first element from sortedWorstCampateListByFondazionaleOrder
    if (sortedWorstCampateListByFondazionaleOrder.length === 1)
      return sortedWorstCampateListByFondazionaleOrder[0];

    // Filter worst campate by PS
    //If there are several spans with the worst Attention Class of the STRUTTURALE E FONDAZIONALE risk,
    //The presence of PS in the L1 defect sheets must be considered.
    const worstCampataFilteredByPS =
      sortedWorstCampateListByFondazionaleOrder.filter(
        (campata) =>
          campata.modelstructure?.[
            BridgeConstants.level2RiskSheets.strutturaleEFondazionale
          ]?.['Stima del livello di vulnerabilita']?.['Presenza di PS'] ===
            'SI' &&
          campata?.[
            BridgeConstants.level2RiskSheets.strutturaleEFondazionale
          ] ===
            sortedWorstCampateListByFondazionaleOrder[0][
              BridgeConstants.level2RiskSheets.strutturaleEFondazionale
            ]
      );

    // If no campata found, return first element from sortedWorstCampateListByFondazionaleOrder
    if (worstCampataFilteredByPS.length === 0)
      return sortedWorstCampateListByFondazionaleOrder[0];

    // If only one campata found, return it
    if (worstCampataFilteredByPS.length === 1)
      return worstCampataFilteredByPS[0];

    // Get max Difettosita relativa
    const maxDifettositaRelativa = Math.max(
      ...worstCampataFilteredByPS.map(
        (campata) =>
          campata.modelstructure?.[
            BridgeConstants.level2RiskSheets.strutturaleEFondazionale
          ]?.['Stima del livello di vulnerabilita']?.['Difettosita relativa']
      )
    );

    // Filter worst campate by Difettosita relativa
    //If multiple spans have the worst Attention Class of STRUTTURALE E FONDAZIONALE risk and 'PS' in L1 defect sheets,
    //consider the span with the highest "DIFETTOSITA’ RELATIVA" (Dr).
    const topDifettositaRelativaCampate = worstCampataFilteredByPS.filter(
      (campata) => {
        const vulnerabilita =
          campata.modelstructure?.[
            BridgeConstants.level2RiskSheets.strutturaleEFondazionale
          ]?.['Stima del livello di vulnerabilita'];
        return (
          vulnerabilita?.['Difettosita relativa'] === maxDifettositaRelativa &&
          vulnerabilita?.['Presenza di PS'] === 'SI'
        );
      }
    );

    // If no campata found, return first element from worstCampataFilteredByPS
    if (topDifettositaRelativaCampate.length === 0)
      return worstCampataFilteredByPS[0];

    // If only one campata found, return first element from topDifettositaRelativaCampate
    if (topDifettositaRelativaCampate.length > 0)
      return topDifettositaRelativaCampate[0];

    return null;
  }

  private getNextInspectionDate(
    periodo: string,
    frequenzaIspettiva: string
  ): string {
    if (periodo === '') return '';

    let interval: number;
    switch (frequenzaIspettiva) {
      case 'In funzione del monitoraggio o trimestrale':
        interval = 3;

        break;

      case 'Semestrale':
      case 'In funzione del monitoraggio o semestrale':
        interval = 6;

        break;

      case '9 mesi':
        interval = 9;

        break;

      case 'Annuale':
        interval = 12;

        break;

      case '18 mesi':
        interval = 18;

        break;

      case 'Biennale':
        interval = 24;

        break;

      default:
        return '';
    }

    // Add interval months to the periodo date
    const periodoDate = new Date(periodo);
    periodoDate.setMonth(periodoDate.getMonth() + interval);

    return moment(periodoDate).format('YYYY-MM-DD');
  }

  private goBack(): void {
    switch (this.model.activeLevel) {
      case BridgeConstants.levels.l0: // Level 0
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[BridgeConstants.levels.l0],
          BridgeConstants.levels.l0
        );
        this.deleteUnusedImages();
        this.location.back();

        break;

      case BridgeConstants.levels.l1: // Level 1
        this.l1UpdateStatus = false;
        delete this.l1UpdateIndex;
        // this.model.activeLevel = BridgeConstants.levels.l0;
        this.switchActiveLevel(BridgeConstants.levels.l0);
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[BridgeConstants.levels.l1],
          BridgeConstants.levels.l1
        );
        this.model[BridgeConstants.levels.l0] = this.l0BackupData;
        this.model[BridgeConstants.levels.l1] = this.l1InitBackupData;
        this.options.formState[BridgeConstants.levels.l1] = true;
        this.options.formState.l1Descrizione = true;
        this.refreshFields();

        break;

      case BridgeConstants.levels.l2: // Level 2 (l2)
        this.l2UpdateStatus = false;
        delete this.l2UpdateIndex;
        delete this.l2CampataNo;
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[BridgeConstants.levels.l2],
          BridgeConstants.levels.l2
        );
        this.model[BridgeConstants.levels.l1] = this.l1BackupData;
        this.resetFields();
        this.options.formState[BridgeConstants.levels.l2] = true;
        this.options.formState.l1Descrizione = true;
        this.options.formState.l1Frane = false;
        // this.model.activeLevel = BridgeConstants.levels.l1;
        this.switchActiveLevel(BridgeConstants.levels.l1);
        this.refreshFields();

        break;

      case BridgeConstants.levels.spalle: // Defects Template - Spalle
      case BridgeConstants.levels.pile: // Defects Template - Pile
      case BridgeConstants.levels.archi: // Defects Template - Archi
      case BridgeConstants.levels.impalcati: // Defects Template - Impalcati
      case BridgeConstants.levels.giunti: // Defects Template - Giunti
      case BridgeConstants.levels.apparecchiSismici: // Defects Template - Apparecchi Sismici
      case BridgeConstants.levels.fileDiAppoggi: // Defects Template - File di Appoggi
        this.l3UpdateStatus = false;
        delete this.l3UpdateIndex;
        this.l3UpdateComponent = '';
        this.l3UpdateDirezione = '';
        delete this.l3UpdateElementNo;
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[this.model.activeLevel],
          this.model.activeLevel
        );
        this.model[this.model.activeLevel][`${this.model.activeLevel} No`] = '';
        if (this.model[this.model.activeLevel].component)
          delete this.model[this.model.activeLevel].component;
        if (this.model[this.model.activeLevel].direzione)
          delete this.model[this.model.activeLevel].direzione;
        if (this.model[this.model.activeLevel].materiale)
          delete this.model[this.model.activeLevel].materiale;

        this.options.formState[this.model.activeLevel] = true;
        // this.model.activeLevel = BridgeConstants.levels.l2;
        this.switchActiveLevel(BridgeConstants.levels.l2);
        this.refreshFields();

        break;

      case BridgeConstants.levels.level2: // Level 2
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[BridgeConstants.levels.level2],
          BridgeConstants.levels.level2
        );
        this.model[BridgeConstants.levels.level2] = this.level2Backup;
        // this.model.activeLevel = BridgeConstants.levels.l0;
        this.switchActiveLevel(BridgeConstants.levels.l0);
        this.isLevel2DataChanged = false;
        this.refreshFields();

        break;

      case BridgeConstants.levels.level2Campata: // Level 2 Campata
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[BridgeConstants.levels.level2Campata],
          BridgeConstants.levels.level2Campata
        );
        this.model[BridgeConstants.levels.level2].campateCdaStatus.campateList =
          this.level2CampateListBackup;
        // this.model.activeLevel = BridgeConstants.levels.level2;
        this.switchActiveLevel(BridgeConstants.levels.level2);
        this.resetLevel2Campate();
        this.refreshFields();

        break;
    }
  }

  private importInspection(appImportedInspectionId: string): void {
    this.inspectionService
      .getInspection(appImportedInspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.model = res.data;
        this.model.appImportedInspectionId = this.appImportedInspectionId;
        this.updateModelForImportInspection();
        this.getTemplateByVersion(this.templateVersion);
        this.generateCampateNoList();
      });
  }

  private isDefectFile(fileName: string): boolean {
    const fileNameSplit = fileName.split(' ');

    return fileNameSplit.length >= 2 &&
      fileNameSplit[0] === 'Foto' &&
      Number(fileNameSplit[1])
      ? true
      : false;
  }

  private isElementiAccessoriPresent(): boolean {
    const isElementiAccessoriPresent = this.model[BridgeConstants.levels.l1][
      'Scheda Ispezione Ponti di Livello 1'
    ].InspectionDate.some((inspectionDate) =>
      Object.values(inspectionDate).some((value) => value)
    );

    return isElementiAccessoriPresent;
  }

  private isLevel1DataChanged(): boolean {
    return (
      JSON.stringify(
        this.getLevel1DataForAttentionClass(
          this.model[BridgeConstants.levels.l0].opereparzialilist[0]
            .modelstructure
        )
      ) !==
      JSON.stringify(
        this.getLevel1DataForAttentionClass(
          this.l0BackupData.opereparzialilist[0].modelstructure
        )
      )
    );
  }

  private async localizzazioneValue(
    level,
    elementNo,
    defect,
    componentNo
  ): Promise<void> {
    if (this.model[level].component) {
      const component = this.getItemFromListByValue(
        ComponentMaterialMappingObj[defect].component,
        this.model[level].component
      );

      // Updating codice elemento
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'codice elemento'
      ] = `${ComponentMaterialMappingObj[defect].element_code}`;

      // Updating component code
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Component code'
      ] = `${component.component_code}`;

      // Updating localizzazione
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Localizzazione'
      ] = `${this.model[level].component} - ${component.component_code}${componentNo}`;

      // Updating fotoCode
      const direzione = this.model[level].direzione
        ? '- ' + DirezioneConfig[this.model[level].direzione].fullName
        : '';

      const fotoCode = `${defect} ${elementNo} ${direzione} (${ComponentMaterialMappingObj[defect].element_code}${elementNo}) - ${this.model[level].component} (${componentNo})`;

      this.model[level]['Scheda Ispezione Ponti di Livello 1'].fotoCode =
        fotoCode;

      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ].forEach((element) => {
        element.fotoName = fotoCode;
      });

      await this.updateExistingUploadedFileNames(fotoCode, level);
      this.refreshFields();
    }
  }

  private migrateDataFromLevel1ToLevel0(): void {
    this.model[BridgeConstants.levels.l1].isElementiAccessoriPresent =
      this.isElementiAccessoriPresent();
    const modelValue = this.getDeepCopy(this.model[BridgeConstants.levels.l1]);

    this.loaderService.requestStarted();
    // this.model.activeLevel = BridgeConstants.levels.l0;
    this.switchActiveLevel(BridgeConstants.levels.l0);
    this.model[BridgeConstants.levels.l2].componentCount ??= {};

    let spalleNo = 0;
    let pileNo = 0;
    let archiNo = 0;
    let impalcatiNo = 0;
    let fileDiAppoggiNo = 0;
    let giuntiNo = 0;
    let apparecchiSismiciNo = 0;

    // TODO: Might not be required
    /* // Level 2 Risk Calculation: Resetting all campata edit status in level 2 campate list
    let level2CampateList =
      this.model[BridgeConstants.levels.l0][BridgeConstants.levels.level2]
        .level2List[0].modelstructure.campateCdaStatus.campateList;
    for (const campata of level2CampateList) {
      campata.isEditDisabled = true;
    } */

    for (let i = 0; i < modelValue.campatelist.length; i++) {
      spalleNo += this.getElementCount(
        modelValue.campatelist[i].modelstructure.spallelist,
        `${BridgeConstants.defectElements.spalle} No`
      );
      pileNo += this.getElementCount(
        modelValue.campatelist[i].modelstructure.pilelist,
        `${BridgeConstants.defectElements.pile} No`
      );
      archiNo += this.getElementCount(
        modelValue.campatelist[i].modelstructure.archilist,
        `${BridgeConstants.defectElements.archi} No`
      );
      impalcatiNo += this.getElementCount(
        modelValue.campatelist[i].modelstructure.impalcatilist,
        `${BridgeConstants.defectElements.impalcati} No`
      );
      fileDiAppoggiNo += this.getElementCount(
        modelValue.campatelist[i].modelstructure.filediappoggilist,
        `${BridgeConstants.defectElements.fileDiAppoggi} No`
      );
      giuntiNo += this.getElementCount(
        modelValue.campatelist[i].modelstructure.giuntilist,
        `${BridgeConstants.defectElements.giunti} No`
      );
      apparecchiSismiciNo += this.getElementCount(
        modelValue.campatelist[i].modelstructure.apparecchisismicilist,
        `${BridgeConstants.defectElements.apparecchiSismici} No`
      );

      // TODO: Might not be required
      /* Level 2 Risk Calculation: Setting campata edit status for added campata in level 2 campate list
      level2CampateList[
        modelValue.campatelist[i][BridgeConstants.partNo] - 1
      ].isEditDisabled = false; */
    }

    // setTimeout(() => {
    // Fetching current child's images and storing into the child's parent
    const savedImagesList = this.storageService.getSavedFilesListFromStorage();
    this.model.sharedData.l0Images =
      this.model.sharedData.l0Images.concat(savedImagesList);

    this.migrateDeletedFilesFromChildToParentLevel(
      BridgeConstants.levels.l0,
      modelValue
    );

    this.setImagesBackToSavedList(BridgeConstants.levels.l1);

    this.updateCampataAttentionClass(modelValue);

    if (this.l1UpdateStatus) {
      // Level 1 edit
      this.model[BridgeConstants.levels.l0].opereparzialilist[
        this.l1UpdateIndex
      ] = {
        'N° campate': modelValue.campatelist.length,
        'N° Spalle': spalleNo,
        'N° Pile': pileNo,
        'N° Archi': archiNo,
        'N° Impalcati': impalcatiNo,
        'N° File di Appoggi': fileDiAppoggiNo,
        'N° Giunti': giuntiNo,
        'N° Apparecchi Sismici': apparecchiSismiciNo,
        modelstructure: modelValue,
      };
      this.model[BridgeConstants.levels.l0].opereparzialilist =
        this.getDeepCopy(
          this.model[BridgeConstants.levels.l0].opereparzialilist
        );
      // check if the L1 data is changed
      if (this.isLevel1DataChanged()) {
        this.resetAllCampataAttentionClass();
      }
      this.l1UpdateStatus = false;
      delete this.l1UpdateIndex;
    } else {
      // Level 1 add
      this.model[BridgeConstants.levels.l0].opereparzialilist = this.model[
        BridgeConstants.levels.l0
      ].opereparzialilist.concat({
        'N° campate': modelValue.campatelist.length,
        'N° Spalle': spalleNo,
        'N° Pile': pileNo,
        'N° Archi': archiNo,
        'N° Impalcati': impalcatiNo,
        'N° File di Appoggi': fileDiAppoggiNo,
        'N° Giunti': giuntiNo,
        'N° Apparecchi Sismici': apparecchiSismiciNo,
        modelstructure: modelValue,
      });
    }

    this.options.formState[BridgeConstants.levels.l1] = true;
    this.options.formState.l1Descrizione = true;
    this.refreshFields();
    this.loaderService.requestEnded();
    // }, this.migrationDelay);
  }

  private async migrateDataFromLevel2ToLevel0(): Promise<void> {
    const modelValue = this.getDeepCopy(
      this.model[BridgeConstants.levels.level2]
    );

    this.loaderService.requestStarted();
    // this.model.activeLevel = BridgeConstants.levels.l0;
    this.switchActiveLevel(BridgeConstants.levels.l0);

    // setting the worst campata
    if (modelValue['campateCdaStatus']?.allCampataAreCalculated) {
      modelValue.worstCampata =
        this.getWorstCampata(
          this.getDeepCopy(modelValue['campateCdaStatus'].campateList)
        ) ?? null;
    }

    const level2List =
      this.model[BridgeConstants.levels.l0][BridgeConstants.levels.level2]
        .level2List;

    if (level2List?.length) {
      level2List[0].modelstructure = modelValue;

      // Updating CdA Status
      level2List[0]['Cda Status'] = modelValue.worstCampata
        ? CdAState.completed
        : CdAState.notCompleted;

      // Setting the attention class for the output report
      if (this.isOnline) {
        const campateCdaStatus =
          level2List[0]?.modelstructure?.['campateCdaStatus'];

        const {
          "classeDiAttenzioneDell'Opera": cdaOpera,
          'Tipologia Opera': tipologiaOpera,
        } = campateCdaStatus;

        if (cdaOpera && tipologiaOpera) {
          const formData = {
            'Globale Opera': {
              cda_Opera: cdaOpera,
              tipologia_opera: tipologiaOpera,
            },
          };

          const response = await this.inspectionService
            .calculateAttentionClass(formData)
            .toPromise();

          const globaleOpera = response.data['Globale Opera'];
          if (globaleOpera) {
            const periodo = this.getPeriodoFromInspectionDate();

            const prossimaIspezione = this.getNextInspectionDate(
              periodo,
              globaleOpera['Frequenza ispettiva']
            );

            campateCdaStatus['Globale Opera'] = {
              ...globaleOpera,
              periodo,
              'Prossima Ispezione': prossimaIspezione,
            };
          }
        }
      }
    }

    // setTimeout(() => {
    // Fetching current child's images and storing into the child's parent
    const savedImagesList = this.storageService.getSavedFilesListFromStorage();
    this.model.sharedData.l0Images =
      this.model.sharedData.l0Images.concat(savedImagesList);

    this.migrateDeletedFilesFromChildToParentLevel(
      BridgeConstants.levels.l0,
      modelValue
    );

    this.setImagesBackToSavedList(BridgeConstants.levels.level2);

    this.model[BridgeConstants.levels.l0][
      BridgeConstants.levels.level2
    ].level2List = this.getDeepCopy(
      this.model[BridgeConstants.levels.l0][BridgeConstants.levels.level2]
        .level2List
    );

    this.refreshFields();
    this.loaderService.requestEnded();
    // }, this.migrationDelay);
  }

  private migrateDataFromLevel2ToLevel1(): void {
    const modelValue = this.getDeepCopy(this.model[BridgeConstants.levels.l2]);

    this.loaderService.requestStarted();
    // this.model.activeLevel = BridgeConstants.levels.l1;
    this.switchActiveLevel(BridgeConstants.levels.l1);
    this.model[BridgeConstants.levels.l2].componentCount ??= {};

    // setTimeout(() => {
    /* // INFO: Tecnico syncing to defect level
      this.model[BridgeConstants.levels.l1].campatelist.forEach((campate, campateIndex) => {
        if (this.l2UpdateIndex === campateIndex) return;

        // Updating repeat sections across L2 under different campate
        const defectProps = this.model.sharedData['defectCensusDate'];
        // Building path list for defects under all elements dynamically
        const defectPaths = [];
        defectProps.forEach((defectItem) => {
          // Adding defects under each element
          const elementListName = [...defectItem.list].pop();
          const defectList = this.cubeService.getPropByPathList(campate, [
            'modelstructure',
            elementListName,
          ]);
          for (let i = 0; i <= defectList.length - 1; i++) {
            // Adding path for each defect
            if (this.l3UpdateIndex === i) return;

            defectPaths.push(
              [
                BridgeConstants.levels.l1,
                'campatelist',
                campateIndex,
                'modelstructure',
                elementListName,
                i,
              ].concat(defectItem.path)
            );
          }
        });
        this.syncRepeatSectionsFromDefectLevels(
          defectPaths,
          this.model[BridgeConstants.levels.l1]['Schede descrittive di ispezione ponti di Livello 1'][
            'Schede descrittive di ispezione ponti di Livello 1 section'
          ]
        );
      }); */

    // Fetching current child's images and storing into the child's parent
    const savedImagesList = this.storageService.getSavedFilesListFromStorage();
    this.model.sharedData.l1Images =
      this.model.sharedData.l1Images.concat(savedImagesList);

    this.migrateDeletedFilesFromChildToParentLevel(
      BridgeConstants.levels.l1,
      modelValue
    );

    this.setImagesBackToSavedList(BridgeConstants.levels.l2);

    modelValue['difettositaRelativaCalculation'] =
      this.calculateDifettositaRelativa(modelValue);

    if (this.l2UpdateStatus) {
      // Level 2 edit
      this.model[BridgeConstants.levels.l1].campatelist[this.l2UpdateIndex] = {
        [BridgeConstants.partNo]: modelValue[BridgeConstants.partNo],
        modelstructure: modelValue,
      };
      this.model[BridgeConstants.levels.l1].campatelist = this.getDeepCopy(
        this.model[BridgeConstants.levels.l1].campatelist
      );

      this.l2UpdateStatus = false;
      delete this.l2UpdateIndex;
      delete this.l2CampataNo;
    } else {
      // Level 2 add
      this.model[BridgeConstants.levels.l1].campatelist = this.model[
        BridgeConstants.levels.l1
      ].campatelist.concat({
        [BridgeConstants.partNo]: modelValue[BridgeConstants.partNo],
        modelstructure: modelValue,
      });
    }

    this.model[BridgeConstants.levels.l2] = {
      newoperaglobale: true,
      spallelist: [],
      pilelist: [],
      archilist: [],
      impalcatilist: [],
      filediappoggilist: [],
      giuntilist: [],
      apparecchisismicilist: [],
    };

    this.options.formState[BridgeConstants.levels.l2] = true;
    this.options.formState.l1Descrizione = true;
    this.options.formState.l1Frane = false;
    this.refreshFields();
    this.loaderService.requestEnded();
    // }, this.migrationDelay);
  }

  private migrateDataFromDefectLevelsToLevel2(defect: string): void {
    this.loaderService.requestStarted();
    const level = this.model.activeLevel;
    const modelValue = this.getDeepCopy(this.model[level]);
    const defectList = `${level}list`;

    // this.model.activeLevel = BridgeConstants.levels.l2;
    this.switchActiveLevel(BridgeConstants.levels.l2);
    this.model[BridgeConstants.levels.l2].componentCount ??= {};

    // setTimeout(() => {
    /* // INFO: Tecnico syncing to defect level
      const censusDate =
        modelValue['Scheda Ispezione Ponti di Livello 1'].InspectionDate;
      // Updating repeat sections across L0, L1
      const propPaths = this.model.sharedData['censusDate'];
      propPaths.forEach((path) => {
        this.syncRepeatSectionsFromDefectLevels(propPaths, censusDate);
      });

      // Updating repeat sections across L2
      const defectProps = this.model.sharedData['defectCensusDate'];
      // Building path list for defects under all elements dynamically
      const defectPaths = [];
      defectProps.forEach((defectItem) => {
        // Adding defects under each element
        const defectList = this.cubeService.getPropByPathList(this.model, defectItem.list);
        for (let i = 0; i <= defectList.length - 1; i++) {
          // Adding path for each defect
          if (this.l3UpdateIndex === i) return;

          defectPaths.push(defectItem.list.concat(i, defectItem.path));
        }
      });
      this.syncRepeatSectionsFromDefectLevels(defectPaths, censusDate); */

    // Fetching current child's images and storing into the child's parent
    const savedImagesList = this.storageService.getSavedFilesListFromStorage();
    this.model.sharedData.l2Images =
      this.model.sharedData.l2Images.concat(savedImagesList);

    this.migrateDeletedFilesFromChildToParentLevel(
      BridgeConstants.levels.l2,
      modelValue
    );

    this.setImagesBackToSavedList(level);

    if (this.l3UpdateStatus) {
      // Level 3 edit
      this.model[BridgeConstants.levels.l2][defectList][this.l3UpdateIndex] = {
        'element no':
          modelValue['Scheda Ispezione Ponti di Livello 1']['Element No'],
        direzione: modelValue.direzione,
        localizazione:
          modelValue['Scheda Ispezione Ponti di Livello 1'].Localizzazione,
        uniquecode:
          modelValue['Scheda Ispezione Ponti di Livello 1'].uniquecode,
        materiale: modelValue.materiale,
        component: modelValue.component,
        modelstructure: modelValue,
      };

      this.model[BridgeConstants.levels.l2][defectList] = this.getDeepCopy(
        this.model[BridgeConstants.levels.l2][defectList]
      );

      this.updateComponentCountForEdit(modelValue, defect, defectList);
      delete this.l3UpdateIndex;
      this.l3UpdateComponent = '';
      this.l3UpdateDirezione = '';
      delete this.l3UpdateElementNo;
      this.l3UpdateStatus = false;
    } else {
      // Level 3 add
      this.model[BridgeConstants.levels.l2][defectList] = this.model[
        BridgeConstants.levels.l2
      ][defectList].concat({
        'element no':
          modelValue['Scheda Ispezione Ponti di Livello 1']['Element No'],
        direzione: modelValue.direzione,
        localizazione:
          modelValue['Scheda Ispezione Ponti di Livello 1'].Localizzazione,
        uniquecode:
          modelValue['Scheda Ispezione Ponti di Livello 1'].uniquecode,
        materiale: modelValue.materiale,
        component: modelValue.component,
        modelstructure: modelValue,
      });

      this.updateComponentCountForAdd(modelValue, defect);
    }

    this.options.formState[level] = true;
    this.refreshFields();
    this.loaderService.requestEnded();
    // }, this.migrationDelay);
  }

  private migrateDataFromRiskSheetsToLevel2(): void {
    const modelValue = this.getDeepCopy(
      this.model[BridgeConstants.levels.level2Campata]
    );
    this.loaderService.requestStarted();
    // this.model.activeLevel = BridgeConstants.levels.level2;
    this.switchActiveLevel(BridgeConstants.levels.level2);

    // setTimeout(() => {
    this.setImagesBackToSavedList(BridgeConstants.levels.level2Campata);

    this.model[BridgeConstants.levels.level2].campateCdaStatus.campateList[
      this.riskIndex
    ] = {
      [BridgeConstants.partNo]: this.riskIndex + 1,
      [BridgeConstants.level2RiskSheets.strutturaleEFondazionale]:
        modelValue?.[
          BridgeConstants.level2RiskSheets.strutturaleEFondazionale
        ]?.['Stima della classe di attenzione strutturale e fondazionale']?.[
          'Classe di attenzione'
        ] ?? '',
      [BridgeConstants.level2RiskSheets.sismica]:
        modelValue?.[BridgeConstants.level2RiskSheets.sismica]?.[
          'Stima della classe di attenzione sismica a livello territoriale'
        ]?.['Classe di attenzione'] ?? '',
      [BridgeConstants.level2RiskSheets.frane]:
        modelValue?.[BridgeConstants.level2RiskSheets.frane]?.[
          'Stima Della Classe Di Attenzione Frane A Livello Territoriale'
        ]?.['Classe Di Attenzione'] ?? '',
      [BridgeConstants.level2RiskSheets.idraulica]:
        modelValue?.[BridgeConstants.level2RiskSheets.idraulica][
          'Stima della classe di attenzione rischio idraulico'
        ]?.['Classe di attenzione rischio idraulico'] ?? '',
      CdA:
        modelValue?.[BridgeConstants.level2RiskSheets.globale]?.[
          'Classe Di Attenzione Globale Della Campata'
        ] ?? '',
      modelstructure: modelValue,
    };

    this.model[BridgeConstants.levels.level2].campateCdaStatus.campateList =
      this.getDeepCopy(
        this.model[BridgeConstants.levels.level2].campateCdaStatus.campateList
      );

    const cdaStatusList = this.getCdAStatusList();
    const allCampataAreCalculated = this.model[
      BridgeConstants.levels.level2
    ].campateCdaStatus.campateList.every((campata) =>
      cdaStatusList.includes(campata.CdA)
    );

    this.model[
      BridgeConstants.levels.level2
    ].campateCdaStatus.allCampataAreCalculated = allCampataAreCalculated;

    if (allCampataAreCalculated) {
      // Find Worst Campata
      const worstCampata = this.getWorstCampata(
        this.getDeepCopy(
          this.model[BridgeConstants.levels.level2].campateCdaStatus.campateList
        )
      );

      // assign the worst campata CdA status to the level2 campateCdaStatus calculated value
      this.model[
        BridgeConstants.levels.level2
      ].campateCdaStatus.calculatedCdAValue = worstCampata?.CdA ?? '';

      // Assign the worst campata CdA status to the level2 campateCdaStatus classeDiAttenzioneDell'Opera.
      this.model[BridgeConstants.levels.level2].campateCdaStatus[
        "classeDiAttenzioneDell'Opera"
      ] = worstCampata?.CdA ?? '';
    } else {
      this.model[
        BridgeConstants.levels.level2
      ].campateCdaStatus.calculatedCdAValue = CdAState.na;
      this.model[BridgeConstants.levels.level2].campateCdaStatus[
        "classeDiAttenzioneDell'Opera"
      ] = CdAState.na;
    }

    // If calculatedCdAValue and classeDiAttenzioneDell'Opera are the same, then rilevamento is calculated; otherwise, rilevamento is modified.
    this.model[BridgeConstants.levels.level2].campateCdaStatus.rilevamento =
      this.model[BridgeConstants.levels.level2].campateCdaStatus
        .calculatedCdAValue ===
      this.model[BridgeConstants.levels.level2].campateCdaStatus[
        "classeDiAttenzioneDell'Opera"
      ]
        ? 'Calculated'
        : 'Modified';

    this.resetLevel2Campate();
    this.refreshFields();
    this.loaderService.requestEnded();
    // }, this.migrationDelay);
  }

  private migrateDeletedFilesFromChildToParentLevel(
    targetLevel: string,
    currentModel: any
  ): void {
    // Fetch deleted files from current level
    const newlyDeletedFiles =
      this.storageService.getDeletedFilesListFromStorage();

    // Fetch deleted files already present in level
    const currentDeletedFiles = currentModel.deletedFiles ?? [];
    delete currentModel.deletedFiles;

    // Migrate deleted files to parent from child
    const targetModel = this.model[targetLevel];
    targetModel.deletedFiles ??= [];
    targetModel.deletedFiles = targetModel.deletedFiles.concat([
      ...currentDeletedFiles,
      ...newlyDeletedFiles,
    ]);

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);
    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
  }

  private async resetCasoNFotoAndCheckbox(
    casoDefectsList: any[],
    casoKey: string
  ): Promise<void> {
    for (const item of casoDefectsList) {
      // Deleting files under N Foto
      if (item['N° foto'] !== '') {
        for (let key of Object.keys(item['N° foto'])) {
          if (key === '') continue;

          await this.fetchCasoImagesAndAddToDeleteList(key);
        }
      }

      // Resetting N Foto
      item['N° foto'] = '';

      // Resetting checkbox
      item.checkbox = item[casoKey] !== 'Altezza (cm)' ? false : '';
    }
  }

  private async resetCasoTableValues(): Promise<void> {
    const casoValuesList = [
      {
        caso: 'Caso1',
        key: 'Marciapiede assente',
      },
      {
        caso: 'Caso2',
        key: 'Assente',
      },
      {
        caso: 'Caso3',
        key: 'Assente',
      },
    ];

    for (const casoValue of casoValuesList) {
      const casoModel = this.model[BridgeConstants.levels.l1][casoValue.caso];

      // For all caso types
      this.resetCasoNFotoAndCheckbox(casoModel.GUARDIAVIA, casoValue.key);

      if (!casoModel.PARAPETTO) continue;

      // For caso2 and caso3
      this.resetCasoNFotoAndCheckbox(casoModel.PARAPETTO, casoValue.key);
    }
  }

  private resetFields(): void {
    this.model[BridgeConstants.levels.l2] = {
      newoperaglobale: true,
      spallelist: [],
      pilelist: [],
      archilist: [],
      impalcatilist: [],
      filediappoggilist: [],
      giuntilist: [],
      apparecchisismicilist: [],
    };
  }

  // Reset user inputted values and calculated values
  private resetLevel2Campate(): void {
    const model = this.getDeepCopy(
      this.model[this.bridgeConstants.levels.level2Campata]
    );
    const resetValues = {
      ...this.getRiskSheetDefaultValues(
        this.bridgeConstants.level2RiskSheets.strutturaleEFondazionale
      ),
      ...this.getRiskSheetDefaultValues(
        this.bridgeConstants.level2RiskSheets.sismica
      ),
      ...this.getRiskSheetDefaultValues(
        this.bridgeConstants.level2RiskSheets.frane
      ),
      ...this.getRiskSheetDefaultValues(
        this.bridgeConstants.level2RiskSheets.idraulica
      ),
      ...this.getRiskSheetDefaultValues(
        this.bridgeConstants.level2RiskSheets.globale
      ),
    };

    this.model[this.bridgeConstants.levels.level2Campata] =
      this.resetLevel2CampateUserInputtedValues(model, resetValues);
  }

  private resetLevel2CampateUserInputtedValues(model: any, resetValues: any) {
    for (const key in resetValues) {
      if (
        model.hasOwnProperty(key) &&
        typeof resetValues[key] === 'object' &&
        resetValues[key] !== null
      ) {
        this.resetLevel2CampateUserInputtedValues(model[key], resetValues[key]);
      } else if (model.hasOwnProperty(key)) {
        model[key] = resetValues[key];
      }
    }

    return model;
  }

  private saveAttentionClassDetailsToL0(): void {
    const campateCdaStatus =
      this.model[BridgeConstants.levels.l0]?.[BridgeConstants.levels.level2]
        ?.level2List?.[0]?.modelstructure?.campateCdaStatus;

    const periodo = this.getPeriodoFromInspectionDate();

    // Setting default values for attentionClassDetails
    this.model[BridgeConstants.levels.l0].attentionClassDetails = {
      'CdA Globale': campateCdaStatus?.["classeDiAttenzioneDell'Opera"] ?? '',
      'Periodicita ispezione':
        campateCdaStatus?.['Globale Opera']?.['Frequenza ispettiva'] ?? '',
      'Prossima ispezione':
        campateCdaStatus?.['Globale Opera']?.['Prossima Ispezione'] ?? '',
      Periodo: periodo ?? '',
      rilevamento: campateCdaStatus?.rilevamento ?? '',
    };
  }

  private saveDefectTotalCountToL0(): void {
    // Setting default values for defectTotalCount
    this.model[BridgeConstants.levels.l0].defectTotalCount = {
      G1: 0,
      G2: 0,
      G3: 0,
      G4: 0,
      G5: 0,
      PS: 0,
    };

    const campateList =
      this.model[BridgeConstants.levels.l0].opereparzialilist?.[0]
        ?.modelstructure.campatelist;
    if (!campateList) return;

    const { defectTotalCount } = this.model[BridgeConstants.levels.l0];

    // Iterating through each campata to calculate defect count
    campateList.forEach(({ modelstructure }) => {
      // Combining all defects list in campata
      const defectsList = [].concat(
        modelstructure[`${BridgeConstants.levels.spalle}list`],
        modelstructure[`${BridgeConstants.levels.pile}list`],
        modelstructure[`${BridgeConstants.levels.archi}list`],
        modelstructure[`${BridgeConstants.levels.impalcati}list`],
        modelstructure[`${BridgeConstants.levels.giunti}list`],
        modelstructure[`${BridgeConstants.levels.apparecchiSismici}list`],
        modelstructure[`${BridgeConstants.levels.fileDiAppoggi}list`]
      );

      // Iterating through each defects list in campata
      defectsList.forEach(({ modelstructure }) => {
        // Iterating through each defect in defects list
        modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Scheda Ispezione Ponti di Livello 1 List1'
        ].forEach((defectRow) => {
          // Updating PS count
          if (defectRow.PS) defectTotalCount.PS += 1;

          // Updating G1 to G5 count based on G value and K1, K2 values
          const k1 = Number(defectRow['Estensione K1']) || 0;
          const k2 = Number(defectRow['Intensità K2']) || 0;
          if (k1 && k2 && defectRow.G) defectTotalCount[`G${defectRow.G}`] += 1;
        });
      });
    });
  }

  private saveDeletedFilesToCurrentLevel(currentModel: any): void {
    // Fetch deleted files from current level
    const newlyDeletedFiles =
      this.storageService.getDeletedFilesListFromStorage();

    // Migrate deleted files to current level
    currentModel.deletedFiles ??= [];
    currentModel.deletedFiles = currentModel.deletedFiles.concat([
      ...newlyDeletedFiles,
    ]);

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);
  }

  private async saveImagesToTable(): Promise<void> {
    const imageList = await this.getFilesFromTempTable();
    let inspectionFiles = [];
    let defectFiles = [];
    let keys: number[] = [];
    for (const image of imageList) {
      keys.push(image.id);
      if (!image.existingImageStatus) {
        if (this.isDefectFile(image.fileName)) defectFiles.push(image.details);
        else inspectionFiles.push(image.details);
      }
    }

    if (inspectionFiles.length)
      await this.idbService.addUploadedFiles(inspectionFiles);

    if (defectFiles.length)
      await this.idbService.addUploadedFiles(defectFiles, StructureType.bridge);

    this.idbService.deleteUploadedFilesFromTempTable(keys);
  }

  private async saveIopRequestDetailsToL0(): Promise<void> {
    const l0 = this.model[BridgeConstants.levels.l0];

    if (!l0.Request?.['IOP Request']) {
      // If request doesn't exist or iop request checkbox is not selected
      // Setting isIopRequestFilled value into L0
      l0['isIopRequestFilled'] = false;

      return;
    }

    // Checking all mandatory fields in request tab
    const isIopRequestFilled =
      l0.Request['Tipo Anagrafica'] &&
      l0.Request['Tipologia Infrastruttura'] &&
      l0.Request['Nome Infrastruttura'] &&
      l0.Request['Codice Infrastruttura'] &&
      l0.Request['Tipologia Opera'] &&
      l0.Request['Nome Opera'] &&
      l0.Request['Codice Opera'] &&
      l0.Request['Codice Catastale del Comune Principale'] &&
      l0.Request['Indirizzo Principale'] &&
      l0.Request['Ente Concedente o Proprietario'] &&
      l0.Request['Concessionario'] &&
      l0.Request["Stato dell'Opera"];

    // Setting isIopRequestFilled value into L0
    l0['isIopRequestFilled'] = isIopRequestFilled;

    if (!isIopRequestFilled) {
      // If L1 tab is active and request tab is not filled then show warning
      await this.getConfirmation({
        isCancelButtonVisible: false,
        message: 'templates.iop_fields_not_filled',
        title: 'confirmation_dialog.title.info',
      });
    }
  }

  private async saveModelToIndexedDb(): Promise<void> {
    this.deleteDuplicateDataFromModel(); // TODO should this be moved to later point??
    this.saveIopRequestDetailsToL0();
    this.saveDefectTotalCountToL0();
    this.saveAttentionClassDetailsToL0();
    await this.deleteUnusedImages();
    await this.saveImagesToTable();
    this.updateRepeatSectionImageIndex();
    this.updateDefectImageIndex();

    if (this.inspectionType === InspectionType.localInspection) {
      this.idbService.updateInspectionModel(
        parseInt(this.idbRecordId),
        JSON.stringify(this.model)
      );
    } else {
      this.idbService.addInspectionModel(this.model);
    }

    this.saveStatus = true;
    this.router.navigate(['/bms-app/to-do']);
    this.cubeService.showMessage(
      MessageType.Info,
      'templates.inspection_save_success'
    );
  }

  private setAllElementoList(): any[] {
    const allElementoList = [];

    Object.values(BridgeCombinedDefects).forEach((defectsList) => {
      defectsList.forEach((defect) => {
        if (
          defect.elemento != null &&
          !allElementoList.includes(defect.elemento)
        ) {
          allElementoList.push(defect.elemento);
        }
      });
    });

    return allElementoList.sort((elemento1, elemento2) => {
      const name1 = elemento1.toLowerCase();
      const name2 = elemento2.toLowerCase();

      if (name1 === name2) return 0;

      return name1 > name2 ? 1 : -1;
    });
  }

  private setCampataAttentionClass(attentionClassDetails: any): void {
    if (!attentionClassDetails) return;

    const {
      [BridgeConstants.level2RiskSheets.strutturaleEFondazionale]:
        strutturaleEFondazionale,
      [BridgeConstants.level2RiskSheets.sismica]: sismica,
      [BridgeConstants.level2RiskSheets.frane]: frane,
      [BridgeConstants.level2RiskSheets.idraulica]: idraulica,
      [BridgeConstants.level2RiskSheets.globale]: globale,
    } = this.model[BridgeConstants.levels.level2Campata];
    const {
      [BridgeConstants.level2RiskSheets.strutturaleEFondazionale]:
        calculatedStrutturaleEFondazionale,
      [BridgeConstants.level2RiskSheets.sismica]: calculatedSismica,
      [BridgeConstants.level2RiskSheets.frane]: calculatedFrane,
      [BridgeConstants.level2RiskSheets.idraulica]: calculatedIdraulica,
      'Globale campata': calculatedGlobale,
    } = attentionClassDetails;

    // Strutturale e fondazionale
    if (calculatedStrutturaleEFondazionale) {
      const pericolosita =
        strutturaleEFondazionale['Stima del livello di pericolosita'];
      const vulnerabilita =
        strutturaleEFondazionale['Stima del livello di vulnerabilita'];
      const esposizione =
        strutturaleEFondazionale['Stima del livello di esposizione'];
      const classeDiAttenzione =
        strutturaleEFondazionale[
          'Stima della classe di attenzione strutturale e fondazionale'
        ];

      pericolosita['Classe di pericolosita'] =
        calculatedStrutturaleEFondazionale["CLASSE DI PERICOLOSITA'"];

      vulnerabilita['Livello di difettosita'] =
        calculatedStrutturaleEFondazionale['Livello di difettosità'];
      vulnerabilita['Classe di vulnerabilita'] =
        calculatedStrutturaleEFondazionale["CLASSE DI VULNERABILITA'"];

      esposizione['Classe di esposizione'] =
        calculatedStrutturaleEFondazionale['CLASSE DI ESPOSIZIONE'];

      classeDiAttenzione['Classe di attenzione'] =
        calculatedStrutturaleEFondazionale['CLASSE DI ATTENZIONE'];
    }

    // Sismica
    if (calculatedSismica) {
      const pericolosita =
        sismica['Valutazione del livello di pericolosità sismica'];
      const vulnerabilita =
        sismica['Stima del livello di vulnerabilità sismica'];
      const esposizione = sismica['Stima del livello di esposizione sismica'];
      const classeDiAttenzione =
        sismica[
          'Stima della classe di attenzione sismica a livello territoriale'
        ];

      pericolosita['Classe di Pericolosita'] =
        calculatedSismica["CLASSE DI PERICOLOSITA' "];

      vulnerabilita['Classe di vulnerabilita'] =
        calculatedSismica["CLASSE DI VULNERABILITA'"];
      vulnerabilita['Livello di difettosità'] =
        calculatedSismica['Livello di difettosità'];

      esposizione['Classe di esposizione'] =
        calculatedSismica['CLASSE DI ESPOSIZIONE '];

      classeDiAttenzione['Classe di attenzione'] =
        calculatedSismica['CLASSE DI ATTENZIONE'];
    }

    // Frane
    if (calculatedFrane) {
      const suscettibilita =
        frane['Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'];
      const vulnerabilita =
        frane['Stima Del Livello Di Vulnerabilità Legato Al Rischio Frane'];
      const esposizione =
        frane['Stima Del Livello Di Esposizione Legato Al Rischio Frane'];
      const classeDiAttenzione =
        frane['Stima Della Classe Di Attenzione Frane A Livello Territoriale'];

      suscettibilita['Classe Di Suscettibilita'] = this.formatCdaClass(
        calculatedFrane[`CLASSE DI SUSCETTIBILITA'`]
      );

      vulnerabilita['Classe Di Vulnerabilita'] = this.formatCdaClass(
        calculatedFrane[`CLASSE DI VULNERABILITA'`]
      );

      esposizione['Classe Di Esposizione Rischio Frane'] = this.formatCdaClass(
        calculatedFrane[`CLASSE DI ESPOSIZIONE RISCHIO FRANE`]
      );

      classeDiAttenzione['Classe Di Attenzione'] = this.formatCdaClass(
        calculatedFrane[`CLASSE DI ATTENZIONE`]
      );
    }

    // Idraulica
    if (calculatedIdraulica) {
      const suscettibilita =
        idraulica['Stima della suscettibilita legeta al rischio idraulico'];
      const vulnerabilita =
        idraulica['Stima della vulnerabilita legeta al rischio idraulico'];
      const esposizione =
        idraulica['Stima dell espezione legata al rischio idraulico'];
      const classeDiAttenzione =
        idraulica['Stima della classe di attenzione rischio idraulico'];

      suscettibilita['Classe di suscettibilita per il fenomeno di sormonto'] =
        this.formatCdaClass(
          calculatedIdraulica[
            "Classe di suscettibilita' per il fenomeno di sormonto"
          ]
        );
      suscettibilita['ca Fattore di restringimento'] = this.convertToPercentage(
        calculatedIdraulica['(Ca) Fattore di restringimento']
      );
      suscettibilita['Cg fattore restringimento delle aree golenali'] =
        this.convertToPercentage(
          calculatedIdraulica['(Cg) fattore restringimento delle aree golenali']
        );

      suscettibilita[
        'Classe di suscettibilita per il fenomeno di erosione generalizzata'
      ] = this.formatCdaClass(
        calculatedIdraulica[
          "Classe di suscettibilita' per il fenomeno di erosione generalizzata"
        ]
      );

      suscettibilita['IEL'] = Number(calculatedIdraulica['IEL'])
        ? parseFloat(calculatedIdraulica['IEL']).toFixed(2)
        : '';
      suscettibilita[
        'Classe di suscettibilita per il fenomeno di erosione localizzata'
      ] = this.formatCdaClass(
        calculatedIdraulica[
          "Classe di suscettibilita' per il fenomeno di erosione localizzata"
        ]
      );

      vulnerabilita['Classe di vulnerabilita per il fenomeno di sormonto'] =
        this.formatCdaClass(
          calculatedIdraulica[
            'Classe di vulnerabilità per il fenomeno di sormonto'
          ]
        );
      vulnerabilita[
        'Classe di vulnerabilita per il fenomeno di erosione generalizzata'
      ] = this.formatCdaClass(
        calculatedIdraulica[
          'Classe di vulnerabilità per il fenomeno di erosione generalizzata'
        ]
      );
      vulnerabilita[
        'Classe di vulnerabilita per il fenomeno di erosione localizzata'
      ] = this.formatCdaClass(
        calculatedIdraulica[
          'Classe di vulnerabilità per il fenomeno di erosione localizzata'
        ]
      );

      esposizione['Classe di esposizione'] = this.formatCdaClass(
        calculatedIdraulica['CLASSE DI ESPOSIZIONE']
      );
      classeDiAttenzione['Classe di attenzione rischio sormonto'] =
        this.formatCdaClass(
          calculatedIdraulica['CLASSE DI ATTENZIONE RISCHIO SORMONTO']
        );
      classeDiAttenzione[
        'Classe di attenzione rischio erosione generalizzata'
      ] = this.formatCdaClass(
        calculatedIdraulica[
          'CLASSE DI ATTENZIONE RISCHIO EROSIONE GENERALIZZATA'
        ]
      );
      classeDiAttenzione['Classe di attenzione rischio erosione localizzata'] =
        this.formatCdaClass(
          calculatedIdraulica[
            'CLASSE DI ATTENZIONE RISCHIO EROSIONE LOCALIZZATA'
          ]
        );
      classeDiAttenzione['Classe di attenzione rischio erosione'] =
        this.formatCdaClass(
          calculatedIdraulica['CLASSE DI ATTENZIONE RISCHIO EROSIONE']
        );

      classeDiAttenzione['Classe di attenzione rischio idraulico'] =
        this.formatCdaClass(
          calculatedIdraulica['CLASSE DI ATTENZIONE RISCHIO IDRAULICO']
        );
    }

    // Globale campata
    if (calculatedGlobale) {
      globale['Classe Di Attenzione Strutturale Fondazionale'] =
        calculatedGlobale['CLASSE DI ATTENZIONE STRUTTURALE-FONDAZIONALE'];
      globale['Classe Di Attenzione Sismica'] =
        calculatedGlobale['CLASSE DI ATTENZIONE SISMICA'];
      globale['Classe Di Attenzione Frane'] =
        calculatedGlobale['CLASSE DI ATTENZIONE FRANE'];
      globale['Classe Di Attenzione Idraulica'] =
        calculatedGlobale['CLASSE DI ATTENZIONE IDRAULICA'];
      globale['Classe Di Attenzione Globale Della Campata'] =
        calculatedGlobale['CLASSE DI ATTENZIONE GLOBALE DELLA CAMPATA'];
    }

    this.refreshFields();
  }

  private setDefects(defectsList: any[], level: string): void {
    const fotoCode =
      this.model[level]['Scheda Ispezione Ponti di Livello 1'].fotoCode;
    defectsList.forEach((element) => {
      element.fotoName = fotoCode;
    });

    this.model[level]['Scheda Ispezione Ponti di Livello 1'][
      'Scheda Ispezione Ponti di Livello 1 List1'
    ] = defectsList;

    this.options.formState[level] = false;

    if (this.l3UpdateStatus) return;

    this.inspectionService.setDefectRowIndex$(
      this.model[BridgeConstants.levels.l2][`${level}list`].length
    );
  }

  private setElementiAccessoriDefects(elementiAccessoriDefects: any): void {
    if (
      this.l1UpdateStatus ||
      this.model[BridgeConstants.levels.l1][
        'Scheda Ispezione Ponti di Livello 1'
      ]['Scheda Ispezione Ponti di Livello 1 List2'].length
    )
      return;

    this.model[BridgeConstants.levels.l1][
      'Scheda Ispezione Ponti di Livello 1'
    ]['Scheda Ispezione Ponti di Livello 1 List2'] =
      elementiAccessoriDefects['Scheda Ispezione Ponti di Livello 1 List2'];
    this.model[BridgeConstants.levels.l1].Caso1.GUARDIAVIA =
      elementiAccessoriDefects.caso1.GUARDIAVIA;
    this.model[BridgeConstants.levels.l1].Caso2.GUARDIAVIA =
      elementiAccessoriDefects.caso2.GUARDIAVIA;
    this.model[BridgeConstants.levels.l1].Caso2.PARAPETTO =
      elementiAccessoriDefects.caso2.PARAPETTO;
    this.model[BridgeConstants.levels.l1].Caso3.GUARDIAVIA =
      elementiAccessoriDefects.caso3.GUARDIAVIA;
    this.model[BridgeConstants.levels.l1].Caso3.PARAPETTO =
      elementiAccessoriDefects.caso3.PARAPETTO;
  }

  private setImageCountEventListener(): void {
    this.inspectionService
      .getImageCount$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((imageCount) => {
        this.model.fotoCount = imageCount;
      });
  }

  // Moving saved images of parent back to savedImageList in storage from model
  private setImagesBackToSavedList(level: string): void {
    switch (level) {
      case BridgeConstants.levels.l1:
        // Moving saved L0 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.l0Images
        );
        this.model.sharedData.l0Images = [];
        this.model.sharedData.l1Images = [];
        break;

      case BridgeConstants.levels.l2:
        // Moving saved L1 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.l1Images
        );
        this.model.sharedData.l1Images = [];
        this.model.sharedData.l2Images = [];
        break;

      case BridgeConstants.levels.spalle: // Defects Template - Spalle
      case BridgeConstants.levels.pile: // Defects Template - Pile
      case BridgeConstants.levels.archi: // Defects Template - Archi
      case BridgeConstants.levels.impalcati: // Defects Template - Impalcati
      case BridgeConstants.levels.giunti: // Defects Template - Giunti
      case BridgeConstants.levels.apparecchiSismici: // Defects Template - Apparecchi Sismici
      case BridgeConstants.levels.fileDiAppoggi: // Defects Template - File di Appoggi
        // Moving saved L2 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.l2Images
        );
        this.model.sharedData.l2Images = [];
        break;

      case BridgeConstants.levels.level2:
        // Moving saved L0 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.l0Images
        );
        this.model.sharedData.l0Images = [];
        this.model.sharedData.level2Images = [];
        break;

      case BridgeConstants.levels.level2Campata:
        // Moving saved Level2 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.level2Images
        );
        this.model.sharedData.level2Images = [];
        break;
    }
  }

  private async setImagesToTempTable(): Promise<void> {
    await this.idbService.clearTempTable(this.model.modelHash);

    const imagesList = await this.idbService.getUploadedFilesList({
      modelHash: this.model.modelHash,
    });
    const defectImagesList = await this.idbService.getUploadedFilesList(
      {
        modelHash: this.model.modelHash,
      },
      StructureType.bridge
    );

    let uploadedFilesList = [];
    uploadedFilesList = imagesList.map((image) => {
      return {
        originalKey: image.id,
        fileName: image.basePathName,
        modelHash: this.model.modelHash,
        existingImageStatus: true,
        details: null,
      };
    });
    uploadedFilesList = uploadedFilesList.concat(
      defectImagesList.map((image) => {
        return {
          originalKey: image.id,
          fileName: image.fotoName,
          modelHash: this.model.modelHash,
          existingImageStatus: true,
          details: null,
        };
      })
    );
    this.idbService.addUploadedFilesToTempTable(uploadedFilesList);
  }

  private setIsOnline(): Promise<void> {
    return this.inspectionService.getOnlineStatus().then((isOnline) => {
      this.cubeService.setOnlineStatus(isOnline);
      this.isOnline = isOnline;
    });
  }

  private setLevel1TipologiaElementiStructures(level1Defects: any): void {
    if (this.l1UpdateStatus) return;

    this.model[BridgeConstants.levels.l1]['Tipologia elementi strutturali'] =
      level1Defects.tipologiaElementiStrutturali;
    this.model[BridgeConstants.levels.l1][
      'Tipologia elementi strutturali impalcato'
    ] = level1Defects.tipologiaElementiStrutturaliImpalcato;
  }

  private setTakeInChargeStatus(): void {
    this.model.status = InspectionStatus.takeInCharge;

    let updateInspectionStatus$;
    if (this.inspectionType === InspectionType.plannedInspection) {
      const data = {
        'Inspection datas': JSON.stringify(this.model),
        planned_inspection_id: this.plannedInspectionId,
      };

      updateInspectionStatus$ =
        this.inspectionService.takeInChargeInspection(data);
    } else if (
      [
        InspectionType.massiveImportedInspection,
        InspectionType.submittedInspection,
      ].includes(this.inspectionType)
    ) {
      const data = new FormData();
      data.append('data', JSON.stringify(this.model));
      updateInspectionStatus$ = this.inspectionService.updateInspectionStatus(
        this.inspectionId,
        data
      );
    }

    updateInspectionStatus$.pipe(takeUntil(this.destroy$)).subscribe(
      (res) => {
        this.model.id = res.data.id;
        this.idbService.addInspectionModel(this.model).then((id) => {
          this.idbRecordId = id;
        });
        this.inspectionType = InspectionType.localInspection;
        this.loaderService.requestEnded();
      },
      (err) => {
        // TODO: verify if the functionality is working correctly
        this.idbService.getInspectionModelsList().then((res) => {
          this.idbService.deleteInspectionModel(res[res.length - 1].id);
          this.router.navigate(['bms-app/to-do/download']);
        });
        this.loaderService.requestEnded();
      }
    );
  }

  private async setTemplate(template: any): Promise<void> {
    // Getting dynamic model structure from template
    const dynamicModelStructure = template.shift();
    if (
      [
        InspectionType.massiveImportedInspection,
        InspectionType.newInspection,
        InspectionType.plannedInspection,
      ].includes(this.inspectionType)
    ) {
      // If new inspection, planned inspection or massiveImportedInspection, adding the dynamic model structure into the model
      // Avoiding this step for other inspection types as dynamicModelStructure is already present in the model and if added again it will overwite the existing l0 and l1 data with empty data
      this.model = {
        ...this.model,
        ...dynamicModelStructure.model,
      };
    }

    // TEMP: For migration
    // If the inspection is app imported or local
    if (
      [
        InspectionType.appImportedInspection,
        InspectionType.localInspection,
      ].includes(this.inspectionType)
    ) {
      // Migrate the model to the latest structure
      const { model } = await this.migrationService.migrateModel(
        this.model,
        dynamicModelStructure.model
      );
      this.model = model;
    }

    // Setting foto count in service to the foto count of this inspection
    this.inspectionService.setImageCount$(this.model.fotoCount);
    this.setImageCountEventListener();

    this.fields = template;
    this.updateModelForNewInspectionVariants();
  }

  private updateComponentCountForAdd(modelValue, element) {
    // Updating component count
    const direzioneNumber = modelValue.direzione
      ? `-${DirezioneConfig[modelValue.direzione].number}`
      : '';
    const elementNo = modelValue[`${element} No`];
    const componentCountKey = `${elementNo}-${element}-${modelValue.component}${direzioneNumber}`;

    if (
      this.model[BridgeConstants.levels.l2].componentCount[componentCountKey]
    ) {
      this.model[BridgeConstants.levels.l2].componentCount[
        componentCountKey
      ] += 1;
    } else {
      this.model[BridgeConstants.levels.l2].componentCount[
        componentCountKey
      ] = 1;
    }
  }

  private updateComponentCountForEdit(modelValue, element, elementList) {
    // change in element no or change in component or change in direzione
    if (
      this.l3UpdateComponent !== modelValue.component ||
      this.l3UpdateElementNo !== modelValue[`${element} No`] ||
      this.l3UpdateDirezione !== modelValue.direzione
    ) {
      // Updating component count
      const direzioneNumber = modelValue.direzione
        ? `-${DirezioneConfig[modelValue.direzione].number}`
        : '';
      const elementNo = `${element} No`;
      const componentCountKey = `${modelValue[elementNo]}-${element}-${modelValue.component}${direzioneNumber}`;

      if (
        this.model[BridgeConstants.levels.l2].componentCount[componentCountKey]
      ) {
        this.model[BridgeConstants.levels.l2].componentCount[
          componentCountKey
        ] += 1;
      } else {
        this.model[BridgeConstants.levels.l2].componentCount[
          componentCountKey
        ] = 1;
      }

      // Updating previous component count
      const updateDirezioneNumber = this.l3UpdateDirezione
        ? `-${DirezioneConfig[this.l3UpdateDirezione].number}`
        : '';
      const updateComponentCountKey = `${this.l3UpdateElementNo}-${element}-${this.l3UpdateComponent}${updateDirezioneNumber}`;

      this.model[BridgeConstants.levels.l2].componentCount[
        updateComponentCountKey
      ] -= 1;

      // Updating unique code for all the remaining defects of the updated element
      this.updateUniqueCodeForRemainingDefects(elementList, element, elementNo);
    }
  }

  private updateEventualiNoteFilesRowIndex(rowIndexUpdate: number): void {
    // Updating rowIndex of each eventuali note file
    this.idbService.updateEventualiNoteFilesRowIndex(
      { modelHash: this.model.modelHash },
      StructureType.bridge,
      rowIndexUpdate
    );
  }

  private async updateExistingUploadedFileNames(
    fotoCode,
    level
  ): Promise<void> {
    const defectList: any[] =
      this.model[level]['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ];
    const imageNameUpdateList$ = [];

    // Updating each filename under the defect template
    for (const defect of defectList) {
      const updatedNFoto = {};
      const key =
        defect['Codice difetto'] === `Eventuali 'Note'` ? 'Note' : 'N° foto';
      const nFotoValues = Object.entries(defect[key]);

      // Updating each filename under the defect template
      for (const [fotoKey, foto] of nFotoValues) {
        const nameParts = fotoKey.split(' - ');

        // Setting foto count and codice difetto
        let updatedFileName = `${nameParts[0]} - ${nameParts[1]}`;

        // Setting descrizione difetto (will be skipped if the image is eventauli note)
        if (key === 'N° foto') {
          updatedFileName += ` - ${nameParts[2]}`;
        }

        // Setting fotoCode (Combination of element, component, material, direzione)
        updatedFileName += ` - ${fotoCode}`;

        // Setting date (only for camera images)
        const dateRegex = /^(\d{2}\/\d{2}\/\d{4} \d{2}:\d{2})$/;
        const formattedDate = nameParts[nameParts.length - 1];
        if (dateRegex.test(formattedDate)) {
          updatedFileName += ` - ${formattedDate}`;
        }

        // Setting section number (only for bridge defects)
        const sectionNoIndex = fotoKey.indexOf(StructureConstants.sectionNo);
        if (sectionNoIndex !== -1) {
          updatedFileName += ` ${fotoKey.slice(sectionNoIndex)}`;
        }

        // Updating generated filenames of files already in indexedDB
        imageNameUpdateList$.push(
          this.idbService.updateDefectFotoNames(
            {
              fotoName: fotoKey,
              modelHash: this.model.modelHash,
            },
            StructureType.bridge,
            updatedFileName
          )
        );

        updatedNFoto[updatedFileName] = foto;
      }

      defect[key] = nFotoValues.length ? updatedNFoto : '';
    }

    await Promise.all(imageNameUpdateList$);
  }

  private updateCampataAttentionClass(l1ModelValue: any): void {
    const cdaCampataList: any[] =
      this.model[BridgeConstants.levels.l0]?.level2?.level2List?.[0]
        ?.modelstructure?.['campateCdaStatus']?.['campateList'] ?? [];

    cdaCampataList.forEach((cdaCampata) => {
      // Updating frane and idraulica tab values
      this.updateInheritedFieldsInLevel2Campata(cdaCampata, l1ModelValue);

      // Updating campata attention class
      const updatedCampata = l1ModelValue.campatelist.find(
        (campata) =>
          campata[BridgeConstants.partNo] === cdaCampata[BridgeConstants.partNo]
      );

      if (!updatedCampata) return;

      const difettositaRelativaCalculation =
        updatedCampata['modelstructure']['difettositaRelativaCalculation'];

      const strutturaleEFondazionale =
        cdaCampata['modelstructure'][
          BridgeConstants.level2RiskSheets.strutturaleEFondazionale
        ];

      //Updating Difettosita relativa in strutturale e fondazionale
      strutturaleEFondazionale['Stima del livello di vulnerabilita'][
        'Difettosita relativa'
      ] = difettositaRelativaCalculation['Difettosita relativa'];

      //Updating Presenza di PS relativa in strutturale e fondazionale
      strutturaleEFondazionale['Stima del livello di vulnerabilita'][
        'Presenza di PS'
      ] = difettositaRelativaCalculation['Presenza di PS'] ? 'SI' : 'NO';
    });
  }

  private async updateDefectImageIndex(): Promise<void> {
    for (const file of this.defectImageListForModification) {
      await this.idbService.updateDefectFileIndex(
        {
          modelHash: this.model.modelHash,
          fotoName: file.fileName,
        },
        file.newIndex,
        StructureType.bridge
      );
    }
  }

  private updateInheritedFieldsInLevel2Campata(cdaCampata, modelValue): void {
    const {
      [BridgeConstants.level2RiskSheets.strutturaleEFondazionale]:
        strutturaleEFondazionale,
      [BridgeConstants.level2RiskSheets.sismica]: sismica,
      [BridgeConstants.level2RiskSheets.frane]: frane,
      [BridgeConstants.level2RiskSheets.idraulica]: idraulica,
    } = cdaCampata['modelstructure'];

    // Updating inherited fields in strutturale e fondazionale tab
    if (strutturaleEFondazionale) {
      const esposizione =
        strutturaleEFondazionale['Stima del livello di esposizione'];

      esposizione['Alternative stradali'] =
        this.model.l0['Rete stradale'][
          'Presenza di alternative stradali in caso di chiusure/limitazioni di traffico'
        ][
          'Presenza di alternative stradali in caso di chiusure/limitazioni di traffico'
        ];
    }

    // Updating inherited fields in sismica tab
    if (sismica) {
      const pericolosita =
        sismica['Valutazione del livello di pericolosità sismica'];
      const vulnerabilita =
        sismica['Stima del livello di vulnerabilità sismica'];

      pericolosita['Accelerazione di picco al suolo'] =
        this.model.l0['Localizzazione']['Sismicità dell’area'];

      vulnerabilita['Elementi di vulnerabilità sismica'] =
        modelValue['Elementi di vulnerabilità sismica'][
          'Elementi di vulnerabilità sismica vedi 4 3 3 delle Linee Guida'
        ]['Assenti/Presenti'];

      const tipoStrutturale = modelValue['Tipologia strutturale'];
      if (tipoStrutturale['Schema isostatico']) {
        vulnerabilita['Schema statico'] = 'Schema isostatico';
      } else if (tipoStrutturale['Schema iperstatico']) {
        vulnerabilita['Schema statico'] = 'Schema iperstatico';
      }
    }

    // Updating inherited fields in frane tab
    if (frane) {
      const suscettibilita =
        frane['Stima Del Livello Di Suscettibilità Legato Al Rischio Frane'];

      const rischioFrane = modelValue['Rischio frane'];
      const parametroDelloStatoFrane =
        rischioFrane[
          'Parametro dello Stato di Attività per la quantificazione di PA frane riconosciute / potenziali'
        ];
      suscettibilita['Instabilità del versante'] = parametroDelloStatoFrane[
        'Parametro dello Stato di Attività per la quantificazione di PA frane riconosciute / potenziali - radio'
      ]
        ? parametroDelloStatoFrane[
            'Parametro dello Stato di Attività per la quantificazione di PA frane riconosciute / potenziali - radio'
          ]
        : '-';
      switch (true) {
        case parametroDelloStatoFrane[
          'Attivo al momento del rilevo o con segni di movimento in atto'
        ] ||
          parametroDelloStatoFrane[
            'Sospesa attivo nell ultimo ciclo stagionale'
          ] ||
          parametroDelloStatoFrane[
            'Quiescente non attivo da più di un ciclo stagionale ma riattivabile'
          ]:
          suscettibilita['Stato di attività'] = 'Attiva';

          break;

        case parametroDelloStatoFrane[
          'Inattiva non attivo da diversi cicli stagionali'
        ]:
          suscettibilita['Stato di attività'] = 'Inattiva';

          break;

        case parametroDelloStatoFrane['Stabilizzata']:
          suscettibilita['Stato di attività'] = 'Stabilizzata';

          break;
      }
      suscettibilita['Massima velocità attesa'] =
        rischioFrane[
          'Parametro della massima velocità attesa in funzione della tipologia di frana in atto o potenziale PV - radio'
        ];
      suscettibilita['Magnitudo attesa su base volumetrica in m3'] =
        rischioFrane[
          'Parametro della magnitudo su base volumetrica in metri cubi PM frane riconosciute / potenziali - radio'
        ];
      suscettibilita['Affidabilità della valutazione'] =
        rischioFrane['Affidabilità complessiva della valutazione'][
          'Affidabilità complessiva della valutazione - radio'
        ];
    }

    // Updating inherited fields in idraulica tab
    if (idraulica) {
      const suscettibilita =
        idraulica['Stima della suscettibilita legeta al rischio idraulico'];
      const vulnerabilita =
        idraulica['Stima della vulnerabilita legeta al rischio idraulico'];

      const rischioIdraulico = modelValue['Rischio idraulico'];
      const tipologiaDiReticolo = rischioIdraulico['Tipologia di reticolo'];
      switch (true) {
        case tipologiaDiReticolo['Reticolo principale']:
          suscettibilita['Corso d acqua'] = 'principale';

          break;

        case tipologiaDiReticolo['Reticolo secondario'] ||
          tipologiaDiReticolo['Reticolo artificiale di scolo']:
          suscettibilita['Corso d acqua'] = 'secondario';

          break;

        default:
          suscettibilita['Corso d acqua'] = '-';

          break;
      }
      const tipoDiAlveo = rischioIdraulico['Tipologia di alveo'];
      suscettibilita['wa l Larghezza alveo a monte del ponte'] =
        tipoDiAlveo[
          'Larghezza complessiva dell’alveo inciso occupata dall’ingombro di pile e spalle wa,l'
        ];
      suscettibilita[
        'wa Larghezza complessiva alveo in corrispondenza del ponte'
      ] =
        tipoDiAlveo[
          'Larghezza complessiva dell’alveo inciso a monte del ponte wa'
        ];
      suscettibilita[
        'Wg l Larghezza complessiva delle golene occupata dai rilevati di accesso'
      ] =
        tipoDiAlveo[
          'Larghezza complessiva delle golene occupata dai rilevati di accesso, dalle spalle e dalle pile wg,l'
        ];
      suscettibilita[
        'Wg Larghezza complessiva delle golene a monte del ponte'
      ] =
        tipoDiAlveo['Larghezza complessiva delle golene a monte del ponte wg'];
      suscettibilita['ds Massima profondità di scavo'] =
        rischioIdraulico['Caratteristiche Alveo'][
          'Massima profondità di scavo ds'
        ];
      suscettibilita[
        'df Profondità di posa del piano di fondazione rispetto all alveo'
      ] =
        rischioIdraulico['Caratteristiche Alveo'][
          'Profondità di posa del piano di fondazione rispetto all’alveo df'
        ];

      const indicareEventuale =
        rischioIdraulico['Caratteristiche Alveo'][
          'Indicare l’eventuale sussistenza delle seguenti condizioni'
        ];
      vulnerabilita[
        'Evidenza di presenza di fondazioni superficiali delle pile e delle spalle del ponte'
      ] = indicareEventuale[
        'Evidenza di presenza di fondazioni superficiali delle pile e delle spalle del ponte'
      ]
        ? 'SI'
        : 'NO';
      vulnerabilita[
        'Evidenza di fenomeni di abbassamento generalizzato dell alveo a monte e a valle del ponte 1'
      ] = indicareEventuale[
        'Evidenza di fenomeni di abbassamento generalizzato dell’alveo a monte e a valle del ponte'
      ]
        ? 'SI'
        : 'NO';
      vulnerabilita[
        'Ponte posizionato in tratto di alveo avente sensibile curvatura'
      ] = indicareEventuale[
        'Ponte posizionato in tratto di alveo avente sensibile curvatura'
      ]
        ? 'SI'
        : 'NO';
      vulnerabilita[
        'Evidenza di fenomeni di abbassamento generalizzato dell alveo a monte e a valle del ponte 2'
      ] = indicareEventuale[
        'Evidenza di fenomeni di abbassamento generalizzato dell’alveo a monte e a valle del ponte'
      ]
        ? 'SI'
        : 'NO';
      vulnerabilita[
        'Presenza di accumuli di detriti o materiale flottante a monte della pila'
      ] = indicareEventuale[
        'Presenza di accumuli di detriti o materiale flottante a monte della pila'
      ]
        ? 'SI'
        : 'NO';
      vulnerabilita['Tendenza dell alveo alla divagazione planimetrica'] =
        indicareEventuale['Tendenza dell’alveo alla divagazione planimetrica']
          ? 'SI'
          : 'NO';
      vulnerabilita[
        'Evidenza di presenza di protezione al piede delle pile e delle spalle del ponte'
      ] = indicareEventuale[
        'Evidenza di presenza di protezione al piede delle pile e delle spalle del ponte'
      ]
        ? 'SI'
        : 'NO';
      vulnerabilita[
        'Presenza di una briglia di protezione immediatamente a valle del ponte'
      ] = indicareEventuale[
        'Presenza di una briglia di protezione immediatamente a valle del ponte'
      ]
        ? 'SI'
        : 'NO';
    }
  }

  private updateModelForImportInspection(): void {
    // Clearing previous inspection informations which is not needed for this inspection
    this.model = {
      ...this.model,
      activeLevel: BridgeConstants.levels.l0,
      created_at: '',
      created_by: '',
      id: '',
      modelHash: this.cubeService.generateUniqueId(),
      status: InspectionStatus.draft,
      updated_at: '',
      updated_by: '',
    };
  }

  private updateModelForNewInspectionVariants(): void {
    if (
      ![
        InspectionType.massiveImportedInspection,
        InspectionType.newInspection,
        InspectionType.plannedInspection,
      ].includes(this.inspectionType)
    )
      return;

    // this.model.templateId = ''; // TODO: not needed???
    this.model.tenantId = this.tenantId;

    switch (this.inspectionType) {
      case InspectionType.newInspection:
        this.model[BridgeConstants.levels.l0].Year = new Date().getFullYear();

        break;

      case InspectionType.plannedInspection:
        delete this.plannedInspection.id;
        this.model = {
          ...this.model,
          structure_type: this.plannedInspection.structure_type,
          [BridgeConstants.levels.l0]: {
            ...this.model[BridgeConstants.levels.l0],
            'Codice IOP': this.plannedInspection.stone_code,
            construction_site: this.plannedInspection.construction_site,
            detector: this.plannedInspection.detector,
            inspection_type: this.plannedInspection.inspection_type,
            'Nome Ponte Viadotto':
              this.plannedInspection.designation_of_the_work,
            planned_inspection_id: this.plannedInspection.id,
            quarter: this.plannedInspection.quarter,
            society: this.plannedInspection.society,
            special_means: this.plannedInspection.special_means,
            tenantId: this.plannedInspection.tenantId,
            'Tipo Opera': this.plannedInspection.type_of_work,
            type_of_work: this.plannedInspection.type_of_work,
            week: this.plannedInspection.week,
            Year: this.plannedInspection.year,
          },
          [BridgeConstants.levels.l1]: {
            ...this.model[BridgeConstants.levels.l1],
            'Schede descrittive di ispezione ponti di Livello 1': {
              ...this.model[BridgeConstants.levels.l1][
                'Schede descrittive di ispezione ponti di Livello 1'
              ],
              'Codice IOP': this.plannedInspection.stone_code,
              'Nome Opera': this.plannedInspection.designation_of_the_work,
            },
            'Schede di ispezione ponti di Livello 1 Fenomeni di frana e fenomeni idraulici':
              {
                ...this.model[BridgeConstants.levels.l1][
                  'Schede di ispezione ponti di Livello 1 Fenomeni di frana e fenomeni idraulici'
                ],
                'Codice IOP': this.plannedInspection.stone_code,
                'Nome Ponte Viadotto':
                  this.plannedInspection.designation_of_the_work,
              },
          },
          // TODO: Here unwanted fields are added into the model but had problems when removed
          ...this.plannedInspection,
        };

        break;

      case InspectionType.massiveImportedInspection:
        // Looping through each key in importStructure
        Object.values(this.model.importStructure).forEach(
          (importPaths: any[]) => {
            // Fetching the value from the massiveImportedInspection
            const value = this.cubeService.getPropByPathList(
              this.massiveImportedInspection,
              importPaths[0]
            );

            // Looping through each path of this key
            importPaths.forEach((importPath) => {
              // Looping through the parts in this path
              importPath.reduce((modelObj, pathKey, i) => {
                if (i === importPath.length - 1) {
                  // If last part in path setting the value to the model
                  modelObj[pathKey] = value;
                } else {
                  // If not last part and path is empty, then setting empty object as part
                  modelObj[pathKey] ??= {};
                }

                // Returning the item from model
                return modelObj[pathKey];
              }, this.model);
            });
          }
        );

        this.generateCampateNoList();
        this.model = this.migrationService.addLevel2ToModel(this.model);

        break;

      default:
        break;
    }
  }

  private async updateRepeatSectionImageIndex(): Promise<void> {
    for (const file of this.repeatSectionImageListForModification) {
      const idbFile = await this.idbService.getUploadedFilesList({
        modelHash: this.model.modelHash,
        basePathName: file.fileName,
      });
      const keyStructureLength = idbFile[0].keyStructure.length;
      idbFile[0].keyStructure[keyStructureLength - 2] =
        file.newIndex.toString();
      await this.idbService.updateRepeatSectionFileIndex(
        {
          modelHash: this.model.modelHash,
          basePathName: file.fileName,
        },
        idbFile[0].keyStructure
      );
    }
  }

  private updateSharedValues(
    updatedValue: any,
    data: any,
    path: string[]
  ): void {
    const pathDeepCopy = this.getDeepCopy(path);
    const pathValue = pathDeepCopy;
    const pathCopy = [...pathValue];
    const key = pathCopy.pop();
    const parent = this.cubeService.getPropByPathList(data, pathCopy);

    if (parent != null && parent[key] != updatedValue) {
      parent[key] = updatedValue;
    }
  }

  private updateUniqueCodeForRemainingDefects(elementList, element, elementNo) {
    let componentNo = 1;
    for (let elementListItem of this.model[BridgeConstants.levels.l2][
      elementList
    ]) {
      const modelStructure = elementListItem.modelstructure;
      if (
        modelStructure.component === this.l3UpdateComponent &&
        modelStructure[elementNo] === this.l3UpdateElementNo &&
        modelStructure.direzione === this.l3UpdateDirezione
      ) {
        const arr = elementListItem.uniquecode.split('.');
        const direzioneCode = this.l3UpdateDirezione
          ? `.${DirezioneConfig[this.l3UpdateDirezione].number}`
          : '';
        const uniqueCode = `${arr[0]}.${arr[1]}.${arr[2]}.${componentNo}${direzioneCode}`;

        // Updating unique code
        elementListItem.uniquecode = uniqueCode;
        modelStructure['Scheda Ispezione Ponti di Livello 1']['Cod unique'] =
          uniqueCode;
        modelStructure['Scheda Ispezione Ponti di Livello 1']['uniquecode'] =
          uniqueCode;

        // Updating Sigla ID
        modelStructure['Scheda Ispezione Ponti di Livello 1']['Sigla ID Elem'] =
          modelStructure['Scheda Ispezione Ponti di Livello 1'][
            'Sigla ID Elem'
          ].slice(0, -1) + componentNo;

        // Updating codice elemento
        modelStructure['Scheda Ispezione Ponti di Livello 1'][
          'codice elemento'
        ] = `${ComponentMaterialMappingObj[element].element_code}`;

        // Updating localizazione
        elementListItem.localizazione = `${modelStructure.component} - ${modelStructure['Scheda Ispezione Ponti di Livello 1']['Component code']}${componentNo}`;

        modelStructure['Scheda Ispezione Ponti di Livello 1'][
          'Localizzazione'
        ] = `${modelStructure.component} - ${modelStructure['Scheda Ispezione Ponti di Livello 1']['Component code']}${componentNo}`;

        // Updating fotoCode
        modelStructure[
          'Scheda Ispezione Ponti di Livello 1'
        ].fotoCode = `${element} ${modelStructure[elementNo]} ${
          modelStructure.direzione
            ? '-' + DirezioneConfig[modelStructure.direzione].fullName
            : ''
        } (${ComponentMaterialMappingObj[element].element_code}${
          modelStructure[`${element} No`]
        }) - ${modelStructure.component} (${componentNo})`;

        componentNo += 1;
      }
    }
  }

  private async validateDefectLevel(): Promise<boolean> {
    const defectsList =
      this.model[this.model.activeLevel]['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ];
    const isDefectListValid = defectsList?.every((defect) => defect.valorized);

    if (
      !this.devMode.disableDefectsValidation && // TEMP: Disable defect validations
      !isDefectListValid
    ) {
      await this.getConfirmation({
        isCancelButtonVisible: false,
        message: 'templates.update_all_defects',
        title: 'confirmation_dialog.title.info',
      });

      return false;
    }

    return true;
  }

  private async validateL0(): Promise<boolean> {
    const l0 = this.model[BridgeConstants.levels.l0];

    // Checking if Codice IOP is filled
    if (!l0['Codice IOP']) {
      await this.getConfirmation({
        isCancelButtonVisible: false,
        message: 'templates.codice_iop_required',
        title: 'confirmation_dialog.title.error',
      });

      return false;
    }

    return true;
  }

  private async validateL1(): Promise<boolean> {
    // Checking if number of campate added in level 1 is same as the unmber of campate added in L0
    const nCampate =
      this.model[BridgeConstants.levels.l0]['Caratteristiche geometriche'][
        'N° campate'
      ];
    if (this.campateCount != nCampate)
      return await this.getConfirmation({
        confirmButton: 'common.buttons.save',
        message: 'templates.campate_no_mismatch_message',
        title: 'templates.campate_no_mismatch_title',
      });

    return true;
  }

  private async validateL2(): Promise<boolean> {
    // Checking if campate number has been selected
    const selectedCampateNo =
      this.model[BridgeConstants.levels.l2][BridgeConstants.partNo];

    // If campata number is not selected
    if (!selectedCampateNo) {
      await this.getConfirmation({
        isCancelButtonVisible: false,
        message: 'templates.add_campata_no',
        title: 'confirmation_dialog.title.info',
      });

      return false;
    }

    // TODO is this needed ???
    // Checking if selected campata number is already used
    const isCampataNoUsed = this.model[
      BridgeConstants.levels.l1
    ].campatelist.some(
      (campata) => campata[BridgeConstants.partNo] === selectedCampateNo
    );
    if (this.l2CampataNo !== selectedCampateNo && isCampataNoUsed) {
      await this.getConfirmation({
        isCancelButtonVisible: false,
        message: 'templates.campata_no_already_added',
        title: 'confirmation_dialog.title.info',
      });

      return false;
    }

    if (!this.l2UpdateStatus) this.campateCount += 1;

    return true;
  }
}
